import React, { useState, useEffect } from 'react';
import { History, Package, Search, Filter, Calendar, User, Download, RotateCcw, ChevronDown, ChevronUp, FileText, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { robotoRegularBase64, robotoBoldBase64 } from '../ecc/roboto';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

// Currency configuration - shared across all components in this module
const currencyConfig = {
  'PLN': { symbol: 'zł', name: 'Polish Złoty' },
  'EUR': { symbol: '€', name: 'Euro' },
  'USD': { symbol: '$', name: 'US Dollar' }
};

// Helper function to get current currency from localStorage
const getCurrentCurrency = () => {
  return localStorage.getItem('warehouse_currency') || 'EUR';
};

// Helper function to get currency symbol
const getCurrencySymbol = () => {
  const currency = getCurrentCurrency();
  return currencyConfig[currency]?.symbol || '€';
};

// Helper function to format price with currency
const formatPrice = (price) => {
  return `${getCurrencySymbol()}${price.toFixed(2)}`;
};

// Car History Modal Component
const CarHistoryModal = ({ historyData, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case '+': return { label: 'Created', color: 'bg-green-100 text-green-800' };
      case '~': return { label: 'Changed', color: 'bg-brand-100 text-brand-800' };
      case '-': return { label: 'Deleted', color: 'bg-red-100 text-red-800' };
      default: return { label: type, color: 'bg-steel-100 text-slate-800' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              <History className="w-5 h-5 mr-2" />
              History - {historyData.car_info}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* History Timeline */}
          <div className="max-h-96 overflow-y-auto">
            {historyData.history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No history records found.
              </div>
            ) : (
              <div className="space-y-4">
                {historyData.history.map((entry, index) => {
                  const typeInfo = getTypeLabel(entry.type);
                  return (
                    <div key={entry.id || index} className="border-l-4 border-brand-500 pl-4 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Date and Type */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-slate-900">
                              {formatDate(entry.date)}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            {entry.item_name && (
                              <span className="text-xs text-slate-500 bg-steel-100 px-2 py-0.5 rounded">
                                {entry.item_name}
                              </span>
                            )}
                          </div>

                          {/* Responsible Person */}
                          <div className="text-sm text-slate-700">
                            <span className="font-medium">Responsible:</span>{' '}
                            <span className="text-brand-600">
                              {entry.responsible_person_name || 'Not assigned'}
                            </span>
                            {entry.responsible_person_2_name && (
                              <span className="text-slate-500">
                                {' '}+ {entry.responsible_person_2_name}
                              </span>
                            )}
                          </div>

                          {/* Status */}
                          <div className="text-sm text-slate-600">
                            <span className="font-medium">Status:</span>{' '}
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              entry.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                              entry.status === 'returned' ? 'bg-green-100 text-green-800' :
                              entry.status === 'lost' ? 'bg-red-100 text-red-800' :
                              entry.status === 'damaged' ? 'bg-orange-100 text-orange-800' :
                              'bg-steel-100 text-slate-800'
                            }`}>
                              {entry.status}
                            </span>
                          </div>

                          {/* Changes */}
                          {entry.changes && entry.changes.length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              Changed: {entry.changes.join(', ')}
                            </div>
                          )}

                          {/* Changed by */}
                          {entry.changed_by && (
                            <div className="text-xs text-slate-400 mt-1">
                              Changed by: {entry.changed_by}
                            </div>
                          )}

                          {/* Notes (show only last part if contains change history) */}
                          {entry.notes && (
                            <div className="mt-2 text-xs text-slate-500 bg-steel-50 p-2 rounded max-h-20 overflow-y-auto">
                              <span className="font-medium">Notes:</span>
                              <pre className="whitespace-pre-wrap font-sans">{entry.notes}</pre>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WarehouseHistory = () => {
  const [issueHistory, setIssueHistory] = useState([]);
  const [groupedHistory, setGroupedHistory] = useState({});
  const [personAssignments, setPersonAssignments] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [showPersonAssignments, setShowPersonAssignments] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [projects, setProjects] = useState([]);
  const [shipmentFilter, setShipmentFilter] = useState('all');
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState('all'); // project/person/all
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [valueRangeFilter, setValueRangeFilter] = useState('all');
  const [itemsCountFilter, setItemsCountFilter] = useState('all');
  const [outstandingFilter, setOutstandingFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false); // Initially hidden to save space
  const [loading, setLoading] = useState(true);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returningProject, setReturningProject] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [expandedIssues, setExpandedIssues] = useState({});
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringProject, setTransferringProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [projectTransfers, setProjectTransfers] = useState({}); // Track transfers: { sourceProjectId: { targetProjectName, date } }
  const [companyFilter, setCompanyFilter] = useState('own'); // all/own/partner - default to 'own'
  const [currentUserCompany, setCurrentUserCompany] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currency, setCurrency] = useState(() => {
    // Load currency from localStorage, default to EUR
    return getCurrentCurrency();
  });
  const [equipmentAssignments, setEquipmentAssignments] = useState([]);
  const [showEquipment, setShowEquipment] = useState(true); // Toggle for equipment visibility
  const [sourceFilter, setSourceFilter] = useState('all'); // all/warehouse/equipment/car
  const [carAssignments, setCarAssignments] = useState([]); // Car inventory assignments
  const [showCarToPersonModal, setShowCarToPersonModal] = useState(false); // Car to person transfer modal
  const [transferringCarData, setTransferringCarData] = useState(null); // Car data being transferred
  const [showCarHistoryModal, setShowCarHistoryModal] = useState(false); // Car history modal
  const [carHistoryData, setCarHistoryData] = useState(null); // Car history data

  useEffect(() => {
    // Load transfer history from localStorage
    const savedTransfers = JSON.parse(localStorage.getItem('projectTransfers') || '{}');
    setProjectTransfers(savedTransfers);

    // Fetch current user's company
    fetchCurrentUserCompany();
    fetchData();

    // Listen for currency changes from WarehouseSettings
    const handleCurrencyChange = (event) => {
      setCurrency(event.detail.currency);
    };

    window.addEventListener('currencyChanged', handleCurrencyChange);

    return () => {
      window.removeEventListener('currencyChanged', handleCurrencyChange);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [issueHistory, equipmentAssignments, carAssignments, searchTerm, statusFilter, projectFilter, dateFromFilter, dateToFilter, shipmentFilter, assignmentTypeFilter, departmentFilter, valueRangeFilter, itemsCountFilter, outstandingFilter, companyFilter, currentUserCompany, sourceFilter]);

  const fetchCurrentUserCompany = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/current/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.data) {
        if (response.data.company_name) {
          setCurrentUserCompany(response.data.company_name);
        }
        // Store full user data for PDF generation
        setCurrentUser(response.data);
      }
    } catch (error) {
      console.error('Error fetching current user company:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchIssueHistory(), fetchProjects(), fetchUsers(), fetchEquipmentAssignments(), fetchCarAssignments()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarAssignments = async () => {
    try {
      console.log('🚗 WAREHOUSE HISTORY - Fetching car inventory assignments...');
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/car-inventory/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const carData = response.data.results || response.data || [];
      console.log('✅ Car Assignments Response:', carData);

      // Filter only active (assigned) items for display
      const activeCarData = carData.filter(ca => ca.status === 'assigned');
      console.log('✅ Active Car Assignments:', activeCarData.length);

      // Group car assignments by car_id to show all items for a car together
      const carGrouped = {};
      activeCarData.forEach(ca => {
        const carId = ca.car;
        if (!carGrouped[carId]) {
          carGrouped[carId] = {
            id: `car_${carId}`,
            issueNumber: `CAR-${carId.toString().padStart(6, '0')}`,
            projectName: null,
            projectId: null,
            issue_type: 'car',
            assigned_to_person: ca.responsible_person_name || null,
            assigned_to_person_2: ca.responsible_person_2_name || null,
            car_info: ca.car_info,
            car_id: carId,
            department: null,
            status: 'assigned',
            issuedAt: ca.assigned_date,
            created_at: ca.assigned_date,
            return_date: null,
            totalValue: 0,
            items: [],
            shipmentId: null,
            shipmentName: null,
            source: 'car',
            responsiblePersons: []
          };
        }

        // Add item to this car's items
        carGrouped[carId].items.push({
          id: ca.id,
          item_id: ca.item,
          item_name: ca.item_name,
          item_sku: ca.item_sku,
          item_picture: ca.item_picture,
          quantity_issued: ca.quantity,
          quantity_returned: 0, // Active items have 0 returned
          unit_price_at_issue: parseFloat(ca.unit_price) || 0,
          notes: ca.notes,
          category_name: ca.category_name,
          subcategory_name: ca.subcategory_name,
          is_hazardous: ca.is_hazardous,
          status: ca.status,
          assigned_date: ca.assigned_date
        });

        // Update responsible persons from active assignments only
        if (ca.responsible_person_name) {
          carGrouped[carId].assigned_to_person = ca.responsible_person_name;
        }
        if (ca.responsible_person_2_name) {
          carGrouped[carId].assigned_to_person_2 = ca.responsible_person_2_name;
        }

        // Use earliest assigned date
        if (new Date(ca.assigned_date) < new Date(carGrouped[carId].issuedAt)) {
          carGrouped[carId].issuedAt = ca.assigned_date;
          carGrouped[carId].created_at = ca.assigned_date;
        }
      });

      // Convert grouped data to array
      const carIssues = Object.values(carGrouped);

      setCarAssignments(carIssues);
    } catch (error) {
      console.error('❌ ERROR fetching car assignments:', error);
      setCarAssignments([]);
    }
  };

  const fetchEquipmentAssignments = async () => {
    try {
      console.log('🔄 WAREHOUSE HISTORY - Fetching equipment assignments...');
      const response = await axios.get(`${API_BASE_URL}/api/equipmentassignment/warehouse-view/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const equipmentData = response.data.results || [];
      console.log('✅ Equipment Assignments Response:', equipmentData);

      // Convert equipment assignments to issue-like format for unified display
      const equipmentIssues = equipmentData.map(eq => ({
        id: `equipment_${eq.id}`,
        issueNumber: `EQ-${eq.id.toString().padStart(6, '0')}`,
        projectName: eq.assignment_type === 'project' ? eq.assigned_to_name : null,
        projectId: eq.assignment_type === 'project' ? eq.assigned_to_id : null,
        issue_type: eq.assignment_type === 'project' ? 'project' : 'person',
        assigned_to_person: eq.assignment_type === 'user' ? eq.assigned_to_name : null,
        department: null,
        status: eq.status,
        issuedAt: eq.assignment_date,
        created_at: eq.assignment_date,
        totalValue: 0, // Equipment doesn't have warehouse value
        items: [{
          id: eq.id,
          item_name: eq.item_name,
          item_description: eq.item_description,
          serial_number: eq.serial_number,
          quantity_issued: eq.quantity,
          quantity_returned: eq.status === 'returned' ? eq.quantity : 0,
          unit_price_at_issue: 0,
          notes: eq.notes,
          equipment_type: eq.equipment_type_name,
          equipment_model: eq.equipment_model_name
        }],
        shipmentId: null,
        shipmentName: null,
        source: 'equipment', // Mark as equipment source
        equipmentData: eq // Keep original data for reference
      }));

      setEquipmentAssignments(equipmentIssues);
    } catch (error) {
      console.error('❌ ERROR fetching equipment assignments:', error);
      // Don't fail silently - equipment is optional, warehouse should still work
      setEquipmentAssignments([]);
    }
  };

  const fetchIssueHistory = async () => {
    try {
      console.log('🔄 WAREHOUSE HISTORY - Fetching data...');
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('Token:', localStorage.getItem('access_token'));
      
      // Helper function to resolve person name from various ID formats
      // Supports: "Person ID: 9", "9", "person_9" formats
      const resolvePersonName = async (assignedToPerson) => {
        if (!assignedToPerson) {
          return assignedToPerson;
        }

        try {
          // Split by comma and process each part
          const parts = assignedToPerson.split(',').map(p => p.trim());
          const resolvedNames = [];

          for (const part of parts) {
            let personId = null;

            if (part.startsWith('Person ID:')) {
              // Format: "Person ID: 9"
              personId = part.replace('Person ID:', '').trim();
            } else if (/^person_(\d+)$/i.test(part)) {
              // Format: "person_9" (legacy format from old transfers)
              personId = part.match(/^person_(\d+)$/i)[1];
            } else if (/^\d+$/.test(part)) {
              // Format: just a number like "9"
              personId = part;
            }

            if (personId) {
              try {
                const userResponse = await axios.get(`${API_BASE_URL}/api/users/${personId}/`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                  }
                });
                const user = userResponse.data;
                resolvedNames.push(`${user.first_name} ${user.last_name}`);
              } catch (error) {
                console.warn('Could not resolve person ID:', personId);
                resolvedNames.push(part); // Keep original if resolution fails
              }
            } else {
              resolvedNames.push(part); // Keep non-ID parts as is (already a name)
            }
          }

          return resolvedNames.join(', ');
        } catch (error) {
          console.warn('Could not resolve person name:', assignedToPerson);
          return assignedToPerson; // Return original if resolution fails
        }
      };
      
      // Fetch both old and new warehouse issues 
      const [oldIssuesResponse, newIssuesResponse, personAssignmentsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/warehouse/issues/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }).catch(() => ({ data: [] })), // Graceful fallback if old endpoint doesn't exist
        axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }),
        axios.get(`${API_BASE_URL}/api/warehouse/person-assignments/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        })
      ]);
      
      console.log('✅ Old Issues Response:', oldIssuesResponse.data);
      console.log('✅ New Issues Response:', newIssuesResponse.data);
      console.log('✅ Person Assignments Response:', personAssignmentsResponse.data);
      
      const oldIssuesData = Array.isArray(oldIssuesResponse.data) ? oldIssuesResponse.data : 
                           oldIssuesResponse.data.results ? oldIssuesResponse.data.results : [];
      const newIssuesData = Array.isArray(newIssuesResponse.data) ? newIssuesResponse.data : 
                           newIssuesResponse.data.results ? newIssuesResponse.data.results : [];
      const personAssignmentsData = Array.isArray(personAssignmentsResponse.data) ? personAssignmentsResponse.data : 
                                   personAssignmentsResponse.data.results ? personAssignmentsResponse.data.results : [];
      
      // Convert old project issues
      const oldProjectIssues = oldIssuesData.map(issue => ({
        id: `old_project_${issue.id}`,
        issueNumber: issue.issue_number,
        projectName: issue.project_name,
        projectId: issue.project,
        issue_type: 'project',
        assigned_to_person: null,
        department: null,
        status: issue.status,
        issuedAt: issue.issued_at,
        created_at: issue.created_at,
        totalValue: parseFloat(issue.total_value || 0),
        items: issue.items || [],
        shipmentId: issue.shipment,
        shipmentName: issue.shipment_name
      }));
      
      // Convert new warehouse issues (both project and person types)
      const newWarehouseIssuesPromises = newIssuesData.map(async (issue) => ({
        id: `warehouse_${issue.id}`,
        issueNumber: issue.issue_number,
        projectName: issue.project_name,
        projectId: issue.project,
        issue_type: issue.issue_type,
        assigned_to_person: await resolvePersonName(issue.assigned_to_person),
        department: issue.department,
        status: issue.status,
        issuedAt: issue.issued_at,
        created_at: issue.created_at,
        totalValue: parseFloat(issue.total_value || 0),
        items: issue.items || [],
        shipmentId: issue.shipment,
        shipmentName: issue.shipment_name,
        issuedByCompanyName: issue.issued_by_company_name,
        // Get unique company names from items
        itemsCompanyNames: [...new Set((issue.items || []).map(item => item.owner_company_name).filter(Boolean))]
      }));
      
      const newWarehouseIssues = await Promise.all(newWarehouseIssuesPromises);
      
      // Convert person assignments to issue-like format
      const personIssues = personAssignmentsData.map(assignment => ({
        id: `person_${assignment.id}`,
        issueNumber: `PA-${assignment.id.toString().padStart(6, '0')}`,
        projectName: null,
        projectId: null,
        issue_type: 'person',
        assigned_to_person: assignment.person_name,
        department: null,
        status: assignment.status,
        issuedAt: assignment.assigned_date,
        created_at: assignment.assigned_date,
        totalValue: 0, // Person assignments don't have total value in this endpoint
        items: [{
          id: assignment.id,
          item_id: assignment.item, // Add item ID for potential transfers
          item_name: assignment.item_name,
          quantity_issued: assignment.quantity,
          quantity_returned: assignment.status === 'returned' ? assignment.quantity : 0,
          unit_price_at_issue: 0,
          notes: assignment.notes,
          individual_items: assignment.individual_items || null
        }],
        shipmentId: null,
        shipmentName: null,
        source: 'warehouse' // Person assignments come from warehouse
      }));
      
      // Combine all types
      // Combine all issues and remove duplicates based on issue_number
      const allIssuesCombined = [...oldProjectIssues, ...newWarehouseIssues, ...personIssues];
      const issueNumberMap = new Map();
      
      // Deduplicate - prefer new warehouse issues over old ones
      allIssuesCombined.forEach(issue => {
        const key = issue.issueNumber;
        if (!issueNumberMap.has(key) || issue.id.includes('warehouse_')) {
          issueNumberMap.set(key, issue);
        }
      });
      
      const allIssues = Array.from(issueNumberMap.values());
      console.log('📋 Combined issues data:', allIssues);
      
      setIssueHistory(allIssues);
    } catch (error) {
      console.error('❌ ERROR fetching issue history:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const projectsData = Array.isArray(response.data) ? response.data :
                          response.data.results ? response.data.results : [];

      // Don't filter projects here - keep all so existing issues can show project names
      // Filter will be applied in dropdowns for new assignments
      setProjects([
        { id: 'all', project_name: 'All Projects' },
        ...projectsData.map(project => ({
          id: project.id,
          project_name: project.name,
          is_active: project.is_active // Keep the is_active flag
        }))
      ]);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const usersData = Array.isArray(response.data) ? response.data :
                       response.data.results ? response.data.results : [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const applyFilters = () => {
    console.log('🔍 APPLYING FILTERS...');
    console.log('Original issueHistory:', issueHistory);
    console.log('issueHistory length:', issueHistory.length);
    console.log('Equipment assignments:', equipmentAssignments.length);
    console.log('Car assignments:', carAssignments.length);
    console.log('🚗 Car assignments data:', carAssignments);
    console.log('🔍 Current filters:', { sourceFilter, assignmentTypeFilter, statusFilter });

    // Combine warehouse issues with equipment and car assignments based on source filter
    let allIssues = [];
    if (sourceFilter === 'all') {
      allIssues = [...issueHistory, ...equipmentAssignments, ...carAssignments];
    } else if (sourceFilter === 'warehouse') {
      allIssues = [...issueHistory];
    } else if (sourceFilter === 'equipment') {
      allIssues = [...equipmentAssignments];
    } else if (sourceFilter === 'car') {
      allIssues = [...carAssignments];
    }

    let filtered = [...allIssues];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(issue =>
        issue.issueNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (issue.projectName && issue.projectName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (issue.assigned_to_person && issue.assigned_to_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (issue.department && issue.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (issue.car_info && issue.car_info.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(issue => issue.status === statusFilter);
    }

    // Project filter
    if (projectFilter !== 'all') {
      filtered = filtered.filter(issue => issue.projectId === parseInt(projectFilter));
    }

    // Shipment filter
    if (shipmentFilter !== 'all') {
      if (shipmentFilter === 'none') {
        filtered = filtered.filter(issue => !issue.shipmentId);
      } else if (shipmentFilter === 'with_shipment') {
        filtered = filtered.filter(issue => issue.shipmentId);
      }
    }

    // Company filter - filter by who issued the warehouse issue (issued_by)
    // Note: Car assignments and person assignments don't have issuedByCompanyName, so we skip filtering for them
    if (companyFilter !== 'all' && currentUserCompany) {
      if (companyFilter === 'own') {
        // Show only issues issued by user's own company
        // For car/person assignments, they belong to user's company by default (API filters by company)
        filtered = filtered.filter(issue =>
          issue.source === 'car' ||
          issue.issue_type === 'person' ||
          issue.issuedByCompanyName === currentUserCompany
        );
      } else if (companyFilter === 'partner') {
        // Show only issues issued by partner companies
        // Car and person assignments are excluded (they're always from user's own company)
        filtered = filtered.filter(issue =>
          issue.source !== 'car' &&
          issue.issue_type !== 'person' &&
          issue.issuedByCompanyName &&
          issue.issuedByCompanyName !== currentUserCompany
        );
      }
    }

    // Date range filter
    if (dateFromFilter) {
      filtered = filtered.filter(issue => 
        issue.issuedAt && new Date(issue.issuedAt) >= new Date(dateFromFilter)
      );
    }

    if (dateToFilter) {
      filtered = filtered.filter(issue => 
        issue.issuedAt && new Date(issue.issuedAt) <= new Date(dateToFilter)
      );
    }

    // Assignment type filter (project/person/all)
    if (assignmentTypeFilter !== 'all') {
      filtered = filtered.filter(issue => issue.issue_type === assignmentTypeFilter);
    }

    // Separate project issues from person, car, and cabinet assignments
    const projectIssues = filtered.filter(issue => issue.issue_type === 'project');
    const personIssues = filtered.filter(issue => issue.issue_type === 'person');
    const carIssues = filtered.filter(issue => issue.issue_type === 'car');
    const cabinetIssues = filtered.filter(issue => issue.issue_type === 'other');

    console.log('🚗 After filtering - carIssues:', carIssues.length, carIssues);

    // Group project issues by project
    const grouped = {};
    projectIssues.forEach(issue => {
      const projectId = issue.projectId;
      if (!grouped[projectId]) {
        grouped[projectId] = {
          projectId: projectId,
          projectName: issue.projectName,
          issues: [],
          totalValue: 0,
          totalItems: 0,
          totalOutstanding: 0,
          type: 'project',
          responsiblePersons: new Set(), // Collect unique responsible persons
          hasEquipment: false, // Track if any issue is from equipment module
          hasWarehouse: false  // Track if any issue is from warehouse
        };
      }
      grouped[projectId].issues.push(issue);
      grouped[projectId].totalValue += issue.totalValue;
      grouped[projectId].totalItems += issue.items.length;

      // Track source types
      if (issue.source === 'equipment') {
        grouped[projectId].hasEquipment = true;
      } else {
        grouped[projectId].hasWarehouse = true;
      }

      // Collect responsible person if exists
      if (issue.assigned_to_person) {
        grouped[projectId].responsiblePersons.add(issue.assigned_to_person);
      }

      // Calculate outstanding items for this issue
      const issueOutstanding = issue.items.reduce((sum, item) =>
        sum + (item.quantity_issued - (item.quantity_returned || 0)), 0
      );
      grouped[projectId].totalOutstanding += issueOutstanding;
    });

    // Convert responsiblePersons Set to Array for each project
    Object.values(grouped).forEach(group => {
      if (group.responsiblePersons) {
        group.responsiblePersons = Array.from(group.responsiblePersons);
      }
    });

    // Group person issues by person (similar to projects)
    personIssues.forEach(issue => {
      const personKey = `person_${issue.assigned_to_person || 'unknown'}`;
      if (!grouped[personKey]) {
        grouped[personKey] = {
          projectId: personKey,
          projectName: `👤 ${issue.assigned_to_person || 'Unknown Person'}`,
          issues: [],
          totalValue: 0,
          totalItems: 0,
          totalOutstanding: 0,
          type: 'person',
          department: issue.department,
          hasEquipment: false, // Track if any issue is from equipment module
          hasWarehouse: false  // Track if any issue is from warehouse
        };
      }
      grouped[personKey].issues.push(issue);
      grouped[personKey].totalValue += issue.totalValue;
      grouped[personKey].totalItems += issue.items.length;

      // Track source types
      if (issue.source === 'equipment') {
        grouped[personKey].hasEquipment = true;
      } else {
        grouped[personKey].hasWarehouse = true;
      }

      // Calculate outstanding items for this issue
      const issueOutstanding = issue.items.reduce((sum, item) =>
        sum + (item.quantity_issued - (item.quantity_returned || 0)), 0
      );
      grouped[personKey].totalOutstanding += issueOutstanding;
    });

    // Group car issues by car
    carIssues.forEach(issue => {
      const carKey = `car_${issue.car_id || 'unknown'}`;
      if (!grouped[carKey]) {
        grouped[carKey] = {
          projectId: carKey,
          projectName: `🚗 ${issue.car_info || 'Unknown Car'}`,
          issues: [],
          totalValue: 0,
          totalItems: 0,
          totalOutstanding: 0,
          type: 'car',
          car_info: issue.car_info,
          car_id: issue.car_id,
          responsiblePersons: new Set(),
          hasEquipment: false,
          hasWarehouse: false,
          hasCar: true
        };
      }
      grouped[carKey].issues.push(issue);
      grouped[carKey].totalValue += issue.totalValue;
      grouped[carKey].totalItems += issue.items.length;

      // Track source types - car inventory items come from warehouse
      if (issue.source === 'car') {
        grouped[carKey].hasCar = true;
        grouped[carKey].hasWarehouse = true; // Car inventory items are warehouse items
      }

      // Collect responsible persons
      if (issue.assigned_to_person) {
        grouped[carKey].responsiblePersons.add(issue.assigned_to_person);
      }
      if (issue.assigned_to_person_2) {
        grouped[carKey].responsiblePersons.add(issue.assigned_to_person_2);
      }

      // Calculate outstanding items for this issue
      const issueOutstanding = issue.items.reduce((sum, item) =>
        sum + (item.quantity_issued - (item.quantity_returned || 0)), 0
      );
      grouped[carKey].totalOutstanding += issueOutstanding;
    });

    // Group cabinet issues (type 'other') by cabinet name
    cabinetIssues.forEach(issue => {
      const cabinetKey = `cabinet_${issue.assigned_to_person || 'unknown'}`;
      if (!grouped[cabinetKey]) {
        grouped[cabinetKey] = {
          projectId: cabinetKey,
          projectName: `🗄️ ${issue.assigned_to_person || 'Unknown Cabinet'}`,
          issues: [],
          totalValue: 0,
          totalItems: 0,
          totalOutstanding: 0,
          type: 'cabinet',
          cabinet_name: issue.assigned_to_person,
          responsiblePersons: new Set(),
          hasEquipment: false,
          hasWarehouse: true,
          hasCabinet: true
        };
      }
      grouped[cabinetKey].issues.push(issue);
      grouped[cabinetKey].totalValue += issue.totalValue;
      grouped[cabinetKey].totalItems += issue.items.length;

      // Calculate outstanding items for this issue
      const issueOutstanding = issue.items.reduce((sum, item) =>
        sum + (item.quantity_issued - (item.quantity_returned || 0)), 0
      );
      grouped[cabinetKey].totalOutstanding += issueOutstanding;
    });

    // Convert responsiblePersons Set to Array for car groups
    Object.values(grouped).forEach(group => {
      if (group.type === 'car' && group.responsiblePersons) {
        group.responsiblePersons = Array.from(group.responsiblePersons);
      }
    });

    // Apply additional filters on grouped data
    let groupedEntries = Object.values(grouped);

    // Department filter (only applies to person assignments)
    if (departmentFilter !== 'all') {
      groupedEntries = groupedEntries.filter(group =>
        group.type === 'project' || group.type === 'car' || group.type === 'cabinet' || group.department === departmentFilter
      );
    }

    // Value range filter
    if (valueRangeFilter !== 'all') {
      switch (valueRangeFilter) {
        case 'under_1000':
          groupedEntries = groupedEntries.filter(group => group.totalValue < 1000);
          break;
        case '1000_5000':
          groupedEntries = groupedEntries.filter(group => group.totalValue >= 1000 && group.totalValue <= 5000);
          break;
        case 'over_5000':
          groupedEntries = groupedEntries.filter(group => group.totalValue > 5000);
          break;
      }
    }

    // Items count filter
    if (itemsCountFilter !== 'all') {
      switch (itemsCountFilter) {
        case 'few':
          groupedEntries = groupedEntries.filter(group => group.totalItems <= 5);
          break;
        case 'medium':
          groupedEntries = groupedEntries.filter(group => group.totalItems > 5 && group.totalItems <= 20);
          break;
        case 'many':
          groupedEntries = groupedEntries.filter(group => group.totalItems > 20);
          break;
      }
    }

    // Outstanding items filter
    if (outstandingFilter !== 'all') {
      switch (outstandingFilter) {
        case 'none':
          groupedEntries = groupedEntries.filter(group => group.totalOutstanding === 0);
          break;
        case 'some':
          groupedEntries = groupedEntries.filter(group => group.totalOutstanding > 0);
          break;
      }
    }

    // Sort to ensure projects come first, then cars, then persons
    groupedEntries.sort((a, b) => {
      const typeOrder = { 'project': 0, 'car': 1, 'person': 2 };
      if (a.type !== b.type) {
        return (typeOrder[a.type] || 3) - (typeOrder[b.type] || 3);
      }
      return (a.projectName || '').localeCompare(b.projectName || ''); // Then alphabetically
    });

    // Convert back to object format with sorted keys
    const filteredGrouped = {};
    groupedEntries.forEach(group => {
      filteredGrouped[group.projectId] = group;
    });

    console.log('📊 FILTER RESULTS:');
    console.log('Project issues:', projectIssues.length);
    console.log('Person issues:', personIssues.length);
    console.log('Car issues:', carIssues.length);
    console.log('Grouped projects, cars and persons:', Object.keys(filteredGrouped));
    console.log('Grouped data:', filteredGrouped);
    
    setPersonAssignments([]); // Teraz wszystko jest w grouped
    setGroupedHistory(filteredGrouped);
    setFilteredProjects(Object.keys(filteredGrouped));
  };

  const handleTransferProject = async (transferData) => {
    try {
      const { sourceProjectData, targetProjectId, responsiblePersonIds, notes } = transferData;

      // Create new assignment for target project with all items from source project
      const issueNumber = `TRANSFER-${Date.now()}`;

      // Collect all items from all issues in source project and group by actualItemId
      const itemsMap = new Map(); // Group by actualItemId to avoid duplicates

      console.log('🚀 TRANSFER DEBUG - sourceProjectData:', sourceProjectData);
      console.log('🚀 TRANSFER DEBUG - sourceProjectData.issues:', sourceProjectData.issues);
      console.log('🚀 TRANSFER DEBUG - issues length:', sourceProjectData.issues?.length);

      sourceProjectData.issues.forEach(issue => {
        console.log('Processing issue:', issue.issueNumber, 'items:', issue.items);
        issue.items.forEach(item => {
          const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
          console.log('Item:', item.item_name, 'outstanding:', quantityOutstanding, 'item data:', item);
          if (quantityOutstanding > 0) {
            // Get the inventory_item ID (not the issue item ID)
            console.log('🔍 Item keys available:', Object.keys(item));
            console.log('🔍 Item inventory_item:', item.inventory_item);
            console.log('🔍 Item item:', item.item);
            console.log('🔍 Item item_id:', item.item_id);
            console.log('🔍 Item warehouse_item_id:', item.warehouse_item_id);

            const itemId = item.inventory_item || item.item || item.item_id || item.warehouse_item_id;
            console.log('Using inventory_item ID:', itemId, 'for item:', item.item_name);

            // Use the itemId directly - should be the inventory_item ID
            let actualItemId = itemId;

            if (!actualItemId) {
              console.error(`❌ No inventory item ID found for ${item.item_name}`);
              console.error(`❌ Available keys:`, Object.keys(item));
              return; // Skip this item
            }

            // Preserve custom field values and individual items from original item
            console.log('🔍 Original item individual items:', item.individual_items);

            // Extract individual items with their custom fields
            let individualItemsToTransfer = [];

            console.log('🔍 item.individual_items:', item.individual_items);
            console.log('🔍 item.custom_fields_summary:', item.custom_fields_summary);

            if (item.individual_items && item.individual_items.length > 0) {
              console.log('🔄 Processing individual items:', item.individual_items.length);
              // Use the actual individual items from the selection
              individualItemsToTransfer = item.individual_items.map(indItem => ({
                customFieldValues: indItem.custom_fields || {},
                serial_number: indItem.serial_number || ''
              }));
            } else {
              console.log('🔄 Processing as bulk item, quantityOutstanding:', quantityOutstanding);
              // Extract custom field values for items without individual tracking
              let customFieldValues = {};
              if (item.custom_fields_summary && item.custom_fields_summary.length > 0) {
                // Flatten custom fields from summary format
                item.custom_fields_summary.forEach(fieldGroup => {
                  Object.entries(fieldGroup).forEach(([fieldName, fieldValue]) => {
                    customFieldValues[fieldName] = fieldValue;
                  });
                });
              }
              // Create individual items based on quantity
              individualItemsToTransfer = Array.from({ length: quantityOutstanding }, () => ({
                customFieldValues: customFieldValues
              }));
            }

            console.log('🔍 individualItemsToTransfer:', individualItemsToTransfer);

            // Group items by actualItemId to avoid duplicate constraint violations
            const key = parseInt(actualItemId);
            if (itemsMap.has(key)) {
              // Add to existing item
              const existingItem = itemsMap.get(key);
              existingItem.quantity += individualItemsToTransfer.length;
              existingItem.individual_items.push(...individualItemsToTransfer);
              console.log(`📦 Grouped item_id ${key}: added ${individualItemsToTransfer.length}, total now ${existingItem.quantity}`);
            } else {
              // Create new item entry
              itemsMap.set(key, {
                item_id: key,
                quantity: individualItemsToTransfer.length,
                notes: notes || `Transferred from project ${sourceProjectData.projectName}`,
                individual_items: individualItemsToTransfer
              });
              console.log(`📦 New item_id ${key}: quantity ${individualItemsToTransfer.length}`);
            }
          }
        });
      });

      // Convert map to array
      const allItems = Array.from(itemsMap.values());
      console.log(`📋 Final grouped items (${allItems.length}):`, allItems.map(item => `ID ${item.item_id}: qty ${item.quantity}`));

      if (allItems.length === 0) {
        alert('No outstanding items to transfer');
        return;
      }

      // Parse targetProjectId to ensure it's a number
      const projectId = parseInt(targetProjectId);

      if (isNaN(projectId)) {
        alert('Invalid target project ID');
        return;
      }

      // Format person IDs to "Person ID: X" format so resolvePersonName can resolve them
      const formattedPersonIds = responsiblePersonIds.length > 0
        ? responsiblePersonIds.map(id => `Person ID: ${id}`).join(', ')
        : '';

      const transferIssueData = {
        issue_number: issueNumber,
        issue_type: 'project',
        status: 'issued',
        project: projectId,  // Use numeric project ID
        purpose: `Transfer from project ${sourceProjectData.projectName}`,
        notes: notes || `Transferred from project ${sourceProjectData.projectName}`,
        assigned_to_person: formattedPersonIds,
        issued_at: new Date().toISOString(),
        items_data: allItems
      };

      console.log('🚀 TRANSFER DEBUG - Creating transfer issue:', transferIssueData);
      console.log('🚀 TRANSFER DEBUG - Transfer items data:', allItems);

      // FIRST: Mark original items as returned to warehouse (this adds quantity back to stock)
      console.log('🔄 STEP 1: Returning items to warehouse from source project...');
      for (const issue of sourceProjectData.issues) {
        for (const item of issue.items) {
          const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
          if (quantityOutstanding > 0) {
            try {
              console.log(`📤 Returning item ${item.id} (${item.item_name}) to warehouse, quantity: ${quantityOutstanding}`);
              await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issue-items/${item.id}/return_quantity/`, {
                quantity_returned: quantityOutstanding,
                condition: 'good',
                notes: `Returned for transfer to project ${targetProjectId}`
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
              console.log(`✅ Successfully returned item ${item.id} to warehouse`);
            } catch (returnError) {
              console.error(`❌ Failed to return item ${item.id}:`, returnError.response?.data || returnError.message);
              throw new Error(`Failed to return item ${item.item_name} to warehouse. Please try again.`);
            }
          }
        }
      }

      console.log('✅ STEP 1 COMPLETE: All items returned to warehouse');

      // THEN: Try different approaches to create assignment (now warehouse has enough stock)
      console.log('🔄 STEP 2: Creating new issue for target project...');
      let createSuccess = false;
      let createdIssue = null;

      // Approach 1: Try warehouse-issues endpoint (like WarehouseInventory)
      try {
        console.log('🔹 Trying warehouse-issues endpoint...');
        const response = await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, transferIssueData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        createdIssue = response.data;
        createSuccess = true;
        console.log('✅ warehouse-issues endpoint worked!', response.data);
      } catch (warehouseError) {
        console.log('❌ warehouse-issues endpoint failed:', warehouseError.response?.status, warehouseError.response?.data);

        // Approach 2: Try old issues endpoint with different structure
        try {
          console.log('🔹 Trying old issues endpoint...');
          const oldFormatData = {
            ...transferIssueData,
            project: projectId,  // Ensure numeric project ID
            items: allItems.map(item => ({
              item: item.item_id,
              quantity_issued: item.quantity,
              notes: item.notes
            }))
          };
          delete oldFormatData.items_data;

          const response = await axios.post(`${API_BASE_URL}/api/warehouse/issues/`, oldFormatData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
          createdIssue = response.data;
          createSuccess = true;
          console.log('✅ Old issues endpoint worked!', response.data);
        } catch (oldError) {
          console.log('❌ Old issues endpoint also failed:', oldError.response?.status, oldError.response?.data);
          throw warehouseError; // Throw original error
        }
      }

      if (!createSuccess) {
        throw new Error('Failed to create transfer assignment');
      }

      console.log('✅ STEP 2 COMPLETE: New issue created for target project');

      // Get target project name
      const targetProject = projects.find(p => p.id === projectId);
      const targetProjectName = targetProject ? targetProject.name : `Project ${projectId}`;

      // Store transfer information
      setProjectTransfers(prev => ({
        ...prev,
        [sourceProjectData.projectId]: {
          targetProjectName: targetProjectName,
          targetProjectId: projectId,
          date: new Date().toISOString(),
          issueNumber: issueNumber
        }
      }));

      // Store in localStorage for persistence
      const existingTransfers = JSON.parse(localStorage.getItem('projectTransfers') || '{}');
      existingTransfers[sourceProjectData.projectId] = {
        targetProjectName: targetProjectName,
        targetProjectId: projectId,
        date: new Date().toISOString(),
        issueNumber: issueNumber
      };
      localStorage.setItem('projectTransfers', JSON.stringify(existingTransfers));

      alert(`Successfully transferred project to new assignment ${issueNumber}`);
      await fetchIssueHistory(); // Refresh data
      setShowTransferModal(false);
      setTransferringProject(null);

    } catch (error) {
      console.error('Error transferring project:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);

      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.response?.data?.error ||
                          JSON.stringify(error.response?.data) ||
                          'Error transferring project. Please try again.';
      alert(`Error transferring project: ${errorMessage}`);
    }
  };

  const fetchCarItemHistory = async (carData) => {
    try {
      // Fetch history for all items in the car
      const historyPromises = [];
      const itemsInfo = [];

      (carData?.issues || []).forEach(issue => {
        (issue.items || []).forEach(item => {
          const carAssignmentId = item.id;
          itemsInfo.push({
            id: carAssignmentId,
            name: item.item_name
          });
          historyPromises.push(
            axios.get(`${API_BASE_URL}/api/warehouse/car-inventory/${carAssignmentId}/history/`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }).catch(err => {
              console.error(`Error fetching history for item ${carAssignmentId}:`, err);
              return { data: { history: [] } };
            })
          );
        });
      });

      const responses = await Promise.all(historyPromises);

      // Combine all history entries with item info
      const allHistory = [];
      responses.forEach((response, index) => {
        const itemInfo = itemsInfo[index];
        (response.data.history || []).forEach(entry => {
          allHistory.push({
            ...entry,
            item_name: response.data.item_name || itemInfo.name,
            car_info: response.data.car_info
          });
        });
      });

      // Sort by date descending
      allHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      setCarHistoryData({
        car_info: carData.car_info || carData.projectName,
        history: allHistory
      });
      setShowCarHistoryModal(true);

    } catch (error) {
      console.error('Error fetching car history:', error);
      alert('Error fetching history. Please try again.');
    }
  };

  const handleChangeCarResponsiblePerson = async (transferData) => {
    try {
      const { carData, targetPersonId, targetPerson2Id, notes } = transferData;

      console.log('🚗 CHANGE CAR RESPONSIBLE PERSON - Starting');
      console.log('Car data:', carData);
      console.log('New responsible person ID:', targetPersonId);
      console.log('New responsible person 2 ID:', targetPerson2Id);

      // Process each item in the car - change responsible person
      for (const issue of carData.issues) {
        for (const item of issue.items) {
          const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);

          if (quantityOutstanding <= 0) {
            console.log(`Skipping ${item.item_name} - no outstanding quantity`);
            continue;
          }

          // Get the car assignment ID (it's stored in item.id for car issues)
          const carAssignmentId = item.id;

          console.log(`Changing responsible person for: ${item.item_name}, carAssignmentId: ${carAssignmentId}`);

          // Change responsible person (history is tracked automatically by django-simple-history)
          await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/${carAssignmentId}/change_responsible_person/`, {
            responsible_person: targetPersonId,
            responsible_person_2: targetPerson2Id || null,
            notes: notes || ''
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });

          console.log(`✅ Successfully changed responsible person for ${item.item_name}`);
        }
      }

      alert('Successfully changed responsible person for car inventory!');
      // Refresh car assignments data
      await fetchCarAssignments();
      setShowCarToPersonModal(false);
      setTransferringCarData(null);

    } catch (error) {
      console.error('Error changing responsible person:', error);
      console.error('Error response data:', error.response?.data);

      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.response?.data?.error ||
                          JSON.stringify(error.response?.data) ||
                          'Error changing responsible person. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleReturnItems = async (returnData) => {
    try {
      // For project-level returns, we need to handle multiple items
      const { projectId, type, returns } = returnData;

      console.log('🔄 handleReturnItems - Processing returns:', returns);
      console.log('🔄 handleReturnItems - Type:', type);

      // Process each return
      for (const itemReturn of returns) {
        console.log(`\n📦 Processing return for ${itemReturn.itemName}:`, {
          quantityToReturn: itemReturn.quantityToReturn,
          quantityOutstanding: itemReturn.quantityOutstanding,
          hasIndividualSelection: itemReturn.hasIndividualSelection,
          selectedIndividualItemsCount: itemReturn.selectedIndividualItems?.length || 0,
          issueItemId: itemReturn.issueItemId,
          type: type
        });

        if (itemReturn.quantityToReturn > 0) {
          // Additional validation before API call
          if (itemReturn.quantityToReturn > itemReturn.quantityOutstanding) {
            console.warn(`Skipping return for ${itemReturn.itemName}: trying to return ${itemReturn.quantityToReturn} but only ${itemReturn.quantityOutstanding} outstanding`);
            continue;
          }

          // Handle car inventory returns differently
          if (type === 'car') {
            console.log(`🚗 Using car-inventory return endpoint for ${itemReturn.itemName}`);
            try {
              // For car inventory, issueItemId is the CarInventoryAssignment ID
              const response = await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/${itemReturn.issueItemId}/return_item/`, {
                notes: itemReturn.notes || ''
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
              console.log(`✅ Successfully returned car inventory item ${itemReturn.itemName}`, response.data);
            } catch (carError) {
              console.error(`❌ Error returning car inventory item ${itemReturn.itemName}:`, carError.response?.data);
              throw carError;
            }
            continue; // Skip to next item - car returns are handled
          }

          // Check if we have individual item selections (for warehouse items)
          if (itemReturn.hasIndividualSelection && itemReturn.selectedIndividualItems.length > 0) {
            console.log(`✅ Using return_items endpoint for ${itemReturn.itemName}`);
            // Use return_items endpoint for individual item returns
            try {
              const itemsPayload = itemReturn.selectedIndividualItems.map(item => ({
                item_id: item.id,  // Add item_id for backend
                custom_fields: item.custom_fields,
                serial_number: item.serial_number,
                condition: item.condition,
                notes: item.notes
              }));

              console.log(`📤 Sending return_items payload for ${itemReturn.itemName}:`, itemsPayload);

              await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issue-items/${itemReturn.issueItemId}/return_items/`, {
                items: itemsPayload
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
            } catch (individualError) {
              console.error(`❌ Error returning individual items for ${itemReturn.itemName}:`, individualError.response?.data);
              throw individualError;
            }
          } else {
            console.log(`📊 Using quantity-based return for ${itemReturn.itemName}`);
            // Use quantity-based return for non-individual items
            try {
              console.log(`🔄 Trying new warehouse endpoint: return_quantity`);
              // First try new warehouse endpoint with quantity-based return
              const response = await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issue-items/${itemReturn.issueItemId}/return_quantity/`, {
                quantity_returned: itemReturn.quantityToReturn,
                condition: itemReturn.condition,
                notes: itemReturn.notes
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
              console.log(`✅ Successfully returned ${itemReturn.quantityToReturn} of ${itemReturn.itemName} using return_quantity endpoint`, response.data);
          } catch (warehouseError) {
            console.error(`❌ Error with new endpoint for item ${itemReturn.issueItemId}:`, warehouseError.response?.data);
            // Fallback to old endpoint for old issues
            try {
              console.log(`🔄 Trying fallback old endpoint: issue-items/return_items`);
              const oldResponse = await axios.post(`${API_BASE_URL}/api/warehouse/issue-items/${itemReturn.issueItemId}/return_items/`, {
                quantity_returned: itemReturn.quantityToReturn,
                condition: itemReturn.condition,
                notes: itemReturn.notes
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
              console.log(`✅ Successfully returned ${itemReturn.quantityToReturn} of ${itemReturn.itemName} using old return_items endpoint`, oldResponse.data);
            } catch (oldError) {
              console.error(`❌ Error with old endpoint for item ${itemReturn.issueItemId}:`, oldError.response?.data);
              throw warehouseError; // Re-throw original error
            }
          }
          } // Close else block
        } // Close if quantityToReturn > 0
      } // Close for loop

      console.log('Return successful, refreshing data...');
      // Refresh both warehouse history and car assignments
      await Promise.all([fetchIssueHistory(), fetchCarAssignments()]);
      console.log('Data refreshed, closing modal...');

      // Force close modal to prevent stale data
      setShowReturnModal(false);
      setReturningProject(null);

      // Re-open modal with fresh data if user wants to continue
      setTimeout(() => {
        alert('Items returned successfully! Modal closed to refresh data. Click "Manage Returns" again if needed.');
      }, 100);
    } catch (error) {
      console.error('Error returning items:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          'Error returning items. Please try again.';
      alert(`Error returning items: ${errorMessage}`);
    }
  };

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleIssueExpansion = (issueId) => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId]
    }));
  };

  const generateBulkPDF = async (projectData) => {
    try {
      // Create PDF content
      const doc = new jsPDF();

      // Add Polish font support
      doc.setLanguage('pl');
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldBase64);
      doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
      doc.setFont('Roboto', 'normal');

      // Header
      doc.setFontSize(20);
      doc.setFont('Roboto', 'bold');
      doc.text('WAREHOUSE BULK ISSUE REPORT', 20, 20);
      doc.setFont('Roboto', 'normal');

      doc.setFontSize(12);

      // Recipient info
      if (projectData.type === 'person') {
        doc.text(`Issued to Person: ${projectData.projectName}`, 20, 35);
        if (projectData.department) {
          doc.text(`Department: ${projectData.department}`, 20, 43);
        }
      } else {
        doc.text(`Project: ${projectData.projectName}`, 20, 35);
      }

      // Summary info in one line (removed Total Issues)
      const summaryY = projectData.type === 'person' && projectData.department ? 53 : 45;
      doc.text(`Total Items: ${projectData.totalItems}  |  Outstanding: ${projectData.totalOutstanding}`, 20, summaryY);

      // Table header for issues
      let yPos = summaryY + 20;
      doc.setFontSize(14);
      doc.text('DETAILED BREAKDOWN:', 20, yPos);
      yPos += 15;
      
      // Process each issue in the project
      projectData.issues.forEach((issue, issueIndex) => {
        // Issue header
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 100);
        doc.text(`${issueIndex + 1}. Issue ${issue.issueNumber}`, 20, yPos);
        doc.setTextColor(0, 0, 0);
        
        // Issue status
        const issueStatusText = issue.status?.toUpperCase().replace('_', ' ') || 'UNKNOWN';
        const issueStatusColor = issue.status === 'issued' ? [0, 100, 0] :
                                issue.status === 'partially_returned' ? [255, 150, 0] :
                                issue.status === 'fully_returned' ? [0, 0, 255] : [0, 0, 0];
        doc.setTextColor(...issueStatusColor);
        doc.text(`(${issueStatusText})`, 120, yPos);
        doc.setTextColor(0, 0, 0);
        
        yPos += 10;
        
        // Issue date
        doc.setFontSize(9);
        doc.text(`Date: ${new Date(issue.issuedAt).toLocaleDateString()}`, 25, yPos);
        if (issue.shipmentName) {
          doc.text(`Shipment: ${issue.shipmentName}`, 120, yPos);
        }
        yPos += 8;
        
        // Items table header (compact) - removed Value column
        doc.setFontSize(8);
        doc.text('Item', 25, yPos);
        doc.text('Issued', 100, yPos);
        doc.text('Returned', 130, yPos);
        doc.text('Outstanding', 160, yPos);
        doc.line(25, yPos + 1, 185, yPos + 1);
        yPos += 8;

        // Items
        issue.items.forEach(item => {
          const quantityIssued = item.quantity_issued || 0;
          const quantityReturned = item.quantity_returned || 0;
          const quantityOutstanding = quantityIssued - quantityReturned;

          // Item name (truncated if too long)
          const itemName = (item.item_name || 'Unknown Item').substring(0, 35);
          doc.text(itemName, 25, yPos);
          doc.text(quantityIssued.toString(), 100, yPos);

          // Color code returned quantity
          if (quantityReturned > 0) {
            doc.setTextColor(0, 150, 0);
            doc.text(quantityReturned.toString(), 130, yPos);
            doc.setTextColor(0, 0, 0);
          } else {
            doc.text('0', 130, yPos);
          }

          // Color code outstanding
          if (quantityOutstanding > 0) {
            doc.setTextColor(255, 100, 0);
            doc.text(quantityOutstanding.toString(), 160, yPos);
            doc.setTextColor(0, 0, 0);
          } else {
            doc.setTextColor(0, 150, 0);
            doc.text('0', 160, yPos);
            doc.setTextColor(0, 0, 0);
          }

          yPos += 6;
          
          // Check if we need a new page
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
        });
        
        yPos += 5; // Space between issues
        
        // Check if we need a new page
        if (yPos > 260) {
          doc.addPage();
          yPos = 20;
        }
      });
      
      // Footer
      doc.setFontSize(10);

      // Get current user full name
      const userFullName = currentUser && currentUser.first_name && currentUser.last_name
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : currentUser?.username || 'N/A';

      doc.text(`Generated on: ${new Date().toLocaleString()}  |  Prepared by: ${userFullName}`, 20, yPos + 10);

      // System signature
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Powered by Ventro.Energy - Modern Warehouse & Equipment Management System  |  ventro.energy', 20, yPos + 18);
      doc.setTextColor(0, 0, 0);

      // Save PDF with appropriate filename
      let filename;
      if (projectData.type === 'person') {
        filename = `bulk-report-person-${projectData.projectName.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
      } else if (projectData.type === 'car') {
        const carName = projectData.projectName.replace(/^🚗\s*/, '').trim();
        filename = `bulk-report-car-${carName.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
      } else {
        filename = `bulk-report-project-${projectData.projectId || 'unknown'}-${Date.now()}.pdf`;
      }
      
      doc.save(filename);
      
    } catch (error) {
      console.error('Error generating bulk PDF:', error);
      alert('Error generating bulk PDF. Please try again.');
    }
  };

  // New function to download bulk PDF from API with modern design
  const downloadBulkPDFFromAPI = async (projectData) => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        alert('Please log in to download PDF');
        return;
      }

      // Build query parameters based on project type
      let queryParams = '';
      if (projectData.type === 'person') {
        // For person assignments - remove emoji from projectName
        // projectName format: "👤 Grzegorz Rakoca" -> "Grzegorz Rakoca"
        const personName = projectData.projectName.replace(/^👤\s*/, '').trim();
        queryParams = `assigned_to_person=${encodeURIComponent(personName)}`;
      } else if (projectData.type === 'car') {
        // For car assignments - use car_id
        queryParams = `car_id=${projectData.car_id}`;
      } else {
        // For projects
        queryParams = `project_id=${projectData.projectId}`;
      }

      console.log('Downloading bulk PDF with params:', queryParams);

      // Call API to generate bulk PDF
      const response = await axios.get(
        `${API_BASE_URL}/api/warehouse/warehouse-issues/download-bulk-pdf/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob', // Important for binary data
        }
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      let filename;
      if (projectData.type === 'person') {
        // Remove emoji from filename
        const personName = projectData.projectName.replace(/^👤\s*/, '').trim();
        filename = `warehouse-bulk-report-${personName.replace(/\s+/g, '-')}.pdf`;
      } else if (projectData.type === 'car') {
        const carName = projectData.projectName.replace(/^🚗\s*/, '').trim();
        filename = `warehouse-bulk-report-car-${carName.replace(/\s+/g, '-')}.pdf`;
      } else {
        filename = `warehouse-bulk-report-project-${projectData.projectId}.pdf`;
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading bulk PDF from API:', error);

      if (error.response?.status === 404) {
        alert('No issues found for this filter. Please try again.');
      } else if (error.response?.status === 403) {
        alert('You do not have permission to download this PDF.');
      } else if (error.response?.status === 400) {
        alert('Invalid parameters for bulk PDF generation.');
      } else {
        // Fallback to old method if API fails
        console.log('Falling back to client-side bulk PDF generation');
        generateBulkPDF(projectData);
      }
    }
  };

  // New function to download PDF from API with modern design
  const downloadPDFFromAPI = async (issueData) => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        alert('Please log in to download PDF');
        return;
      }

      // Get issue ID from issueData
      // ID can be in format: "warehouse_123", "old_project_456", "person_789"
      // We need to extract the numeric ID
      let issueId = issueData.id;

      if (!issueId) {
        console.error('No issue ID found in issueData:', issueData);
        alert('Cannot download PDF: Issue ID not found');
        return;
      }

      // Extract numeric ID from prefixed strings
      if (typeof issueId === 'string') {
        if (issueId.startsWith('warehouse_')) {
          issueId = issueId.replace('warehouse_', '');
        } else if (issueId.startsWith('old_project_')) {
          // Old project issues use different endpoint - not supported yet
          alert('PDF download not available for old project issues. Please use the legacy system.');
          return;
        } else if (issueId.startsWith('person_')) {
          // Person assignments might not have warehouse issue - not supported yet
          alert('PDF download not available for person assignments yet.');
          return;
        }
      }

      console.log('Downloading PDF for issue ID:', issueId);

      // Call API to generate PDF
      const response = await axios.get(
        `${API_BASE_URL}/api/warehouse/warehouse-issues/${issueId}/download-pdf/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob', // Important for binary data
        }
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `warehouse-issue-${issueData.issueNumber || issueId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading PDF from API:', error);

      if (error.response?.status === 404) {
        alert('Issue not found. Please try again.');
      } else if (error.response?.status === 403) {
        alert('You do not have permission to download this PDF.');
      } else {
        // Fallback to old method if API fails
        console.log('Falling back to client-side PDF generation');
        generatePDF(issueData);
      }
    }
  };

  const generatePDF = async (issueData) => {
    try {
      // Create PDF content
      const doc = new jsPDF();

      // Add Polish font support
      doc.setLanguage('pl');
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldBase64);
      doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
      doc.setFont('Roboto', 'normal');

      // Header
      doc.setFontSize(20);
      doc.setFont('Roboto', 'bold');
      doc.text('WAREHOUSE ISSUE REPORT', 20, 20);
      doc.setFont('Roboto', 'normal');

      doc.setFontSize(12);
      doc.text(`Issue Number: ${issueData.issueNumber}`, 20, 35);

      // Add recipient info - either project or person
      if (issueData.projectName) {
        doc.text(`Project: ${issueData.projectName}`, 20, 45);
      } else if (issueData.assigned_to_person) {
        doc.text(`Issued to Person: ${issueData.assigned_to_person}`, 20, 45);
        if (issueData.department) {
          doc.text(`Department: ${issueData.department}`, 20, 53);
        }
      }

      // Status with color coding
      const statusColors = {
        'issued': [0, 100, 0], // Green
        'partially_returned': [255, 150, 0], // Orange
        'fully_returned': [0, 0, 255], // Blue
        'cancelled': [255, 0, 0] // Red
      };
      const statusText = issueData.status?.toUpperCase().replace('_', ' ') || 'UNKNOWN';
      const color = statusColors[issueData.status] || [0, 0, 0];
      doc.setTextColor(...color);
      doc.text(`Status: ${statusText}`, 120, 35);
      doc.setTextColor(0, 0, 0); // Reset to black

      const dateY = (issueData.assigned_to_person && issueData.department) ? 63 : 55;
      doc.text(`Date: ${new Date(issueData.issuedAt).toLocaleDateString()}`, 20, dateY);

      // Table header (removed prices)
      doc.text('ITEMS DETAILS:', 20, dateY + 20);
      doc.setFontSize(10);
      doc.text('Item Name', 20, dateY + 30);
      doc.text('Issued', 100, dateY + 30);
      doc.text('Returned', 130, dateY + 30);
      doc.text('Outstanding', 160, dateY + 30);

      // Draw line under header
      doc.line(20, dateY + 32, 190, dateY + 32);
      
      // Table content
      let yPos = dateY + 40;
      issueData.items.forEach(item => {
        const quantityIssued = item.quantity_issued || 0;
        const quantityReturned = item.quantity_returned || 0;
        const quantityOutstanding = quantityIssued - quantityReturned;

        // Item name
        doc.text(item.item_name || 'Unknown Item', 20, yPos);

        // Quantities
        doc.text(quantityIssued.toString(), 100, yPos);

        // Returned quantity with color
        if (quantityReturned > 0) {
          doc.setTextColor(0, 150, 0); // Green for returned
          doc.text(quantityReturned.toString(), 130, yPos);
          doc.setTextColor(0, 0, 0);
        } else {
          doc.text('0', 130, yPos);
        }

        // Outstanding quantity with color
        if (quantityOutstanding > 0) {
          doc.setTextColor(255, 100, 0); // Orange for outstanding
          doc.text(quantityOutstanding.toString(), 160, yPos);
          doc.setTextColor(0, 0, 0);
        } else {
          doc.setTextColor(0, 150, 0); // Green if fully returned
          doc.text('0', 160, yPos);
          doc.setTextColor(0, 0, 0);
        }
        
        // Add status text if applicable
        if (quantityReturned === quantityIssued) {
          doc.setFontSize(8);
          doc.setTextColor(0, 150, 0);
          doc.text('(Fully Returned)', 20, yPos + 4);
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
        } else if (quantityReturned > 0) {
          doc.setFontSize(8);
          doc.setTextColor(255, 150, 0);
          doc.text('(Partially Returned)', 20, yPos + 4);
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
        }
        
        yPos += 12;
      });
      
      doc.setFontSize(12);

      // Footer
      doc.setFontSize(10);

      // Get current user full name
      const userFullName = currentUser && currentUser.first_name && currentUser.last_name
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : currentUser?.username || 'N/A';

      doc.text(`Generated on: ${new Date().toLocaleString()}  |  Prepared by: ${userFullName}`, 20, yPos + 20);

      // System signature
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Powered by Ventro.Energy - Modern Warehouse & Equipment Management System  |  ventro.energy', 20, yPos + 28);
      doc.setTextColor(0, 0, 0);

      // Save PDF
      doc.save(`warehouse-issue-${issueData.issueNumber}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };


  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setProjectFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setShipmentFilter('all');
    setCompanyFilter('all');
    setAssignmentTypeFilter('all');
    setDepartmentFilter('all');
    setValueRangeFilter('all');
    setItemsCountFilter('all');
    setOutstandingFilter('all');
    setSourceFilter('all');
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'issued', label: 'Issued' },
    { value: 'assigned', label: 'Assigned (Equipment)' },
    { value: 'received', label: 'Received (Equipment)' },
    { value: 'partially_returned', label: 'Partially Returned' },
    { value: 'fully_returned', label: 'Fully Returned' },
    { value: 'returned', label: 'Returned (Equipment)' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'lost', label: 'Lost (Equipment)' },
    { value: 'damaged', label: 'Damaged (Equipment)' }
  ];

  if (loading) {
    return (
      <div className="p-6 bg-steel-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-steel-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-steel-300 rounded w-1/2 mb-8"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-steel-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-steel-50 min-h-screen">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Allocation Status
              </h1>
              <p className="text-slate-600 mt-2">Track all issued items and manage returns</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-900">{filteredProjects.length}</div>
              <div className="text-sm text-slate-500">Total Groups</div>
              <div className="text-xs text-slate-400 mt-1">
                Projects & Persons Combined
              </div>
            </div>
          </div>
        </div>

        {/* Quick Type Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-200 p-1.5 mb-6">
          <nav className="flex gap-1" aria-label="Type filter tabs">
            <button
              onClick={() => setAssignmentTypeFilter('all')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                assignmentTypeFilter === 'all'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-steel-100 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                All
              </span>
            </button>
            <button
              onClick={() => setAssignmentTypeFilter('person')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                assignmentTypeFilter === 'person'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-steel-100 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Persons
              </span>
            </button>
            <button
              onClick={() => setAssignmentTypeFilter('project')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                assignmentTypeFilter === 'project'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-steel-100 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Projects
              </span>
            </button>
            <button
              onClick={() => setAssignmentTypeFilter('car')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                assignmentTypeFilter === 'car'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-steel-100 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 5h8m-4 5h4M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
                </svg>
                Cars
              </span>
            </button>
            <button
              onClick={() => setAssignmentTypeFilter('other')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                assignmentTypeFilter === 'other'
                  ? 'bg-orange-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-steel-100 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Cabinets
              </span>
            </button>
          </nav>
        </div>

        {/* Compact Search Bar with Expandable Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-200 mb-6">
          {/* Always visible search bar */}
          <div className="p-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by issue number, project, or person..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-2 border border-brand-200 rounded-xl hover:bg-brand-50 transition-colors transition-colors flex items-center gap-1"
                title={showFilters ? "Hide advanced filters" : "Show advanced filters"}
              >
                <Filter className="h-4 w-4" />
                {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Expandable filters section */}
          {showFilters && (
            <div className="border-t border-brand-200 p-4 transition-all duration-300 ease-in-out">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Filter - Warehouse vs Equipment */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Sources</option>
                <option value="warehouse">📦 Warehouse Only</option>
                <option value="equipment">🛡️ Equipment/PPE Only</option>
                <option value="car">🚗 Car Inventory Only</option>
              </select>
            </div>

            {/* Assignment Type Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <select
                value={assignmentTypeFilter}
                onChange={(e) => setAssignmentTypeFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="project">Projects</option>
                <option value="person">Persons</option>
                <option value="car">Cars</option>
                <option value="other">Cabinets</option>
              </select>
            </div>

            {/* Shipment Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Shipment</label>
              <select
                value={shipmentFilter}
                onChange={(e) => setShipmentFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Issues</option>
                <option value="with_shipment">With Shipment</option>
                <option value="none">No Shipment</option>
              </select>
            </div>

            {/* Company Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Companies</option>
                <option value="own">My Company Only</option>
                <option value="partner">Partner Companies Only</option>
              </select>
            </div>

            {/* Outstanding Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Outstanding</label>
              <select
                value={outstandingFilter}
                onChange={(e) => setOutstandingFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="none">Fully Returned</option>
                <option value="some">Has Outstanding</option>
              </select>
            </div>

            {/* Value Range Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Value Range</label>
              <select
                value={valueRangeFilter}
                onChange={(e) => setValueRangeFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Values</option>
                <option value="under_1000">Under $1,000</option>
                <option value="1000_5000">$1,000 - $5,000</option>
                <option value="over_5000">Over $5,000</option>
              </select>
            </div>

            {/* Items Count Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Items Count</label>
              <select
                value={itemsCountFilter}
                onChange={(e) => setItemsCountFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Counts</option>
                <option value="few">1-5 Items</option>
                <option value="medium">6-20 Items</option>
                <option value="many">20+ Items</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
              <input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 text-slate-600 border border-brand-200 rounded-xl hover:bg-brand-50 transition-colors transition-colors flex items-center justify-center"
                title="Clear all filters"
              >
                <Filter className="h-4 w-4 mr-1" />
                Clear
              </button>
            </div>
          </div>
          </div>
        )}
        </div>

        {/* Project History Table */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-100">
              <thead className="bg-steel-100 border-b-2 border-brand-500">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Issues</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Outstanding</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-brand-100">
                {filteredProjects.map((projectId) => {
                  const projectData = groupedHistory[projectId];
                  const isExpanded = expandedProjects[projectId];
                  
                  return (
                    <React.Fragment key={projectId}>
                      {/* Project Row */}
                      <tr className="hover:bg-brand-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleProjectExpansion(projectId)}
                            className="flex items-center text-left w-full"
                          >
                            <svg
                              className={`h-4 w-4 mr-2 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div>
                              <div className="text-sm font-medium text-slate-900 flex items-center flex-wrap gap-1">
                                {projectData.projectName}
                                {/* Source badges */}
                                {projectData.hasEquipment && (
                                  <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded" title="Contains Equipment/PPE items">
                                    🛡️ Equipment
                                  </span>
                                )}
                                {projectData.hasWarehouse && projectData.hasEquipment && (
                                  <span className="ml-1 text-xs bg-brand-100 text-brand-800 px-1.5 py-0.5 rounded" title="Contains Warehouse items">
                                    📦 Warehouse
                                  </span>
                                )}
                                {projectData.type === 'person' && projectData.department && (
                                  <span className="text-xs text-brand-600 ml-2">({projectData.department})</span>
                                )}
                                {(projectData.type === 'project' || projectData.type === 'car') && projectData.responsiblePersons && projectData.responsiblePersons.length > 0 && (
                                  <span
                                    className="text-xs text-green-600 ml-2 flex items-center cursor-help"
                                    title={projectData.responsiblePersons.join(', ')}
                                  >
                                    👤 {projectData.responsiblePersons.length === 1
                                      ? projectData.responsiblePersons[0]
                                      : `${projectData.responsiblePersons.length} persons`}
                                  </span>
                                )}
                                {/* Show transferred badge */}
                                {projectTransfers[projectId] && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800 group relative">
                                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                                    Transferred → {projectTransfers[projectId].targetProjectName}
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Remove transfer info
                                        const newTransfers = { ...projectTransfers };
                                        delete newTransfers[projectId];
                                        setProjectTransfers(newTransfers);
                                        localStorage.setItem('projectTransfers', JSON.stringify(newTransfers));
                                      }}
                                      className="ml-1 hover:text-brand-900 cursor-pointer"
                                      title="Clear transfer badge"
                                      role="button"
                                      tabIndex={0}
                                    >
                                      ×
                                    </span>
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500">
                                {projectData.type === 'person' ? 'Person Assignment' : projectData.type === 'car' ? 'Car Assignment' : `Project ID: ${projectId}`}
                                {projectTransfers[projectId] && (
                                  <span className="ml-2 text-brand-600">
                                    Transfer date: {new Date(projectTransfers[projectId].date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{projectData.issues.length} shipments</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{projectData.totalItems} items</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium">
                            <span className={projectData.totalOutstanding > 0 ? 'text-yellow-600' : 'text-green-600'}>
                              {projectData.totalOutstanding} items
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">{formatPrice(projectData.totalValue)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {/* Return button - only for warehouse items, not equipment */}
                            {projectData.totalOutstanding > 0 && projectData.hasWarehouse && (
                              <button
                                onClick={() => {
                                  setReturningProject(projectData);
                                  setShowReturnModal(true);
                                }}
                                className="text-green-600 hover:text-green-900"
                                title="Manage Returns (Warehouse items only)"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            {/* Equipment-only projects show info icon */}
                            {projectData.totalOutstanding > 0 && projectData.hasEquipment && !projectData.hasWarehouse && (
                              <span
                                className="text-orange-500 cursor-help"
                                title="Equipment returns are managed in the Equipment module"
                              >
                                🛡️
                              </span>
                            )}
                            {projectData.totalOutstanding > 0 && projectData.type === 'project' && projectData.hasWarehouse && (
                              <button
                                onClick={() => {
                                  setTransferringProject(projectData);
                                  setShowTransferModal(true);
                                }}
                                className="text-brand-600 hover:text-brand-900"
                                title="Transfer to Another Project"
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </button>
                            )}
                            {/* Change responsible person for car inventory */}
                            {projectData.totalOutstanding > 0 && projectData.type === 'car' && (
                              <button
                                onClick={() => {
                                  setTransferringCarData(projectData);
                                  setShowCarToPersonModal(true);
                                }}
                                className="text-brand-600 hover:text-brand-900"
                                title="Change Responsible Person"
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </button>
                            )}
                            {/* History for car inventory */}
                            {projectData.type === 'car' && (
                              <button
                                onClick={() => fetchCarItemHistory(projectData)}
                                className="text-slate-600 hover:text-slate-900"
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadBulkPDFFromAPI(projectData)}
                              className="text-brand-600 hover:text-brand-900"
                              title="Download Bulk Report (All Issues) - Modern design with company logo"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Issues */}
                      {isExpanded && projectData.issues.map((issue) => {
                        const isIssueExpanded = expandedIssues[issue.id];
                        return (
                          <React.Fragment key={issue.id}>
                            <tr 
                              className="bg-steel-50 hover:bg-steel-100 cursor-pointer"
                              onClick={() => toggleIssueExpansion(issue.id)}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                setSelectedIssue(issue);
                                setShowDetailModal(true);
                              }}
                              title="Single click to expand items, double click for details"
                            >
                              <td className="px-6 py-3 pl-14 whitespace-nowrap">
                                <div className="flex items-center">
                                  <button className="mr-2 p-1">
                                    {isIssueExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-slate-400" />
                                    ) : (
                                      <ChevronUp className="h-4 w-4 text-slate-400" />
                                    )}
                                  </button>
                                  <div className="text-sm text-slate-700">
                                    {issue.source === 'equipment' ? (
                                      <span className="text-orange-500 mr-1">🛡️</span>
                                    ) : (
                                      <Package className="h-4 w-4 inline mr-1 text-slate-400" />
                                    )}
                                    {issue.issueNumber}
                                    {issue.source === 'equipment' && (
                                      <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">
                                        Equipment
                                      </span>
                                    )}
                                    {issue.assigned_to_person && (
                                      <div className="text-xs text-brand-600 mt-1">
                                        👤 {issue.assigned_to_person}
                                      </div>
                                    )}
                                    {issue.shipmentName && (
                                      <div className="text-xs text-brand-600 mt-1">
                                        📦 Shipment: {issue.shipmentName}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  issue.status === 'issued' ? 'bg-brand-100 text-brand-800' :
                                  issue.status === 'assigned' ? 'bg-brand-100 text-brand-800' :
                                  issue.status === 'received' ? 'bg-indigo-100 text-indigo-800' :
                                  issue.status === 'partially_returned' ? 'bg-yellow-100 text-yellow-800' :
                                  issue.status === 'fully_returned' ? 'bg-green-100 text-green-800' :
                                  issue.status === 'returned' ? 'bg-green-100 text-green-800' :
                                  issue.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  issue.status === 'lost' ? 'bg-red-100 text-red-800' :
                                  issue.status === 'damaged' ? 'bg-orange-100 text-orange-800' :
                                  'bg-steel-100 text-slate-800'
                                }`}>
                                  {issue.status.replace('_', ' ').toUpperCase()}
                                </span>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="text-sm text-slate-600">{issue.items.length} items</div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="text-sm text-slate-600">
                                  {issue.items.reduce((sum, item) => sum + (item.quantity_issued - (item.quantity_returned || 0)), 0)} outstanding
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="text-sm text-slate-600">{formatPrice(issue.totalValue)}</div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadPDFFromAPI(issue);
                                    }}
                                    className="text-brand-600 hover:text-brand-900"
                                    title="Download PDF Report (with company logo and signature fields)"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Expanded Items */}
                            {isIssueExpanded && (
                              <tr>
                                <td colSpan="6" className="px-0 py-0">
                                  <div className="bg-brand-25 border-l-4 border-brand-200">
                                    <div className="px-6 py-4">
                                      <h4 className="text-sm font-medium text-slate-900 mb-3">
                                        Issued Items for {issue.issueNumber}
                                      </h4>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-brand-100">
                                          <thead className="bg-steel-100 border-b-2 border-brand-500">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Item</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Additional Fields</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Issued</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Returned</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Outstanding</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Unit Price</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Total Value</th>
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-brand-100">
                                            {issue.items.map((item, itemIndex) => {
                                              console.log('🔍 RENDERING ITEM:', item.item_name, {
                                                full_item: item,
                                                has_individual_items: !!item.individual_items,
                                                individual_items_length: item.individual_items?.length,
                                                individual_items: item.individual_items,
                                                has_custom_fields_summary: !!item.custom_fields_summary,
                                                custom_fields_summary_length: item.custom_fields_summary?.length,
                                                custom_fields_summary: item.custom_fields_summary
                                              });

                                              const quantityIssued = item.quantity_issued || 0;
                                              const quantityReturned = item.quantity_returned || 0;
                                              const quantityOutstanding = quantityIssued - quantityReturned;
                                              const unitPrice = parseFloat(item.unit_price_at_issue) || 0;
                                              const totalValue = quantityIssued * unitPrice;

                                              return (
                                                <tr
                                                  key={`${item.id}-${itemIndex}`}
                                                  className="hover:bg-brand-50 transition-colors cursor-pointer"
                                                  onDoubleClick={() => {
                                                    setSelectedIssue({...issue, selectedItem: item});
                                                    setShowDetailModal(true);
                                                  }}
                                                  title="Double-click for item details"
                                                >
                                                  <td className="px-3 py-2 text-sm text-slate-900">
                                                    {item.item_name || 'Unknown Item'}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    {item.custom_fields_summary && item.custom_fields_summary.length > 0 ? (
                                                      <div className="space-y-1">
                                                        {item.custom_fields_summary.map((fieldGroup, groupIdx) => {
                                                          const isRetired = fieldGroup._retired === true;
                                                          const serialNumber = fieldGroup._serial_number;
                                                          return (
                                                            <div
                                                              key={groupIdx}
                                                              className={`text-xs p-2 rounded border ${
                                                                isRetired
                                                                  ? 'bg-red-50 border-red-300'
                                                                  : 'bg-steel-50 border-brand-200'
                                                              }`}
                                                            >
                                                              {isRetired && (
                                                                <div className="text-red-600 font-semibold mb-1">
                                                                  🚫 RETIRED
                                                                </div>
                                                              )}
                                                              {serialNumber && (
                                                                <div>
                                                                  <span className="font-medium">Serial:</span> {serialNumber}
                                                                </div>
                                                              )}
                                                              {Object.entries(fieldGroup).map(([fieldName, fieldValue], idx) => {
                                                                // Skip internal fields
                                                                if (fieldName.startsWith('_')) return null;
                                                                // Remove _item_xxxxx suffix from field name (everything after _item_)
                                                                const cleanFieldName = fieldName.replace(/_item_.*$/i, '');
                                                                return (
                                                                  <div key={idx}>
                                                                    <span className="font-medium">{cleanFieldName}:</span> {fieldValue}
                                                                  </div>
                                                                );
                                                              })}
                                                            </div>
                                                          );
                                                        })}
                                                      </div>
                                                    ) : item.individual_items && item.individual_items.length > 0 ? (
                                                      (() => {
                                                        // Filter individual items to only show those with meaningful data
                                                        const itemsWithData = item.individual_items.filter(indItem =>
                                                          indItem.serial_number ||
                                                          (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                                                        );

                                                        return itemsWithData.length > 0 ? (
                                                          <div className="space-y-1">
                                                            {itemsWithData.map((indItem, idx) => (
                                                              <div
                                                                key={idx}
                                                                className={`text-xs p-2 rounded border ${
                                                                  indItem.retired
                                                                    ? 'bg-red-50 border-red-300'
                                                                    : 'bg-steel-50 border-brand-200'
                                                                }`}
                                                              >
                                                                {indItem.retired && (
                                                                  <div className="text-red-600 font-semibold mb-1">
                                                                    🚫 RETIRED
                                                                  </div>
                                                                )}
                                                                {indItem.serial_number && (
                                                                  <div>
                                                                    <span className="font-medium">Serial:</span> {indItem.serial_number}
                                                                  </div>
                                                                )}
                                                                {indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0 && (
                                                                  <div>
                                                                    {Object.entries(indItem.custom_fields).map(([key, value]) => {
                                                                      const cleanKey = key.replace(/_item_.*$/i, '');
                                                                      return (
                                                                        <div key={key}>
                                                                          <span className="font-medium">{cleanKey}:</span> {value}
                                                                        </div>
                                                                      );
                                                                    })}
                                                                  </div>
                                                                )}
                                                              </div>
                                                            ))}
                                                          </div>
                                                        ) : (
                                                          <span className="text-xs text-slate-500">No additional fields</span>
                                                        );
                                                      })()
                                                    ) : (
                                                      <span className="text-xs text-slate-500">No additional fields</span>
                                                    )}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    {/* Status column - show summary instead of individual statuses */}
                                                    {(() => {
                                                      // Calculate status summary from individual items or custom_fields_summary
                                                      let statusCounts = {};

                                                      if (item.individual_items && item.individual_items.length > 0) {
                                                        item.individual_items.forEach(indItem => {
                                                          const status = indItem.status || 'issued';
                                                          statusCounts[status] = (statusCounts[status] || 0) + 1;
                                                        });
                                                      } else if (item.custom_fields_summary && item.custom_fields_summary.length > 0) {
                                                        item.custom_fields_summary.forEach(fieldGroup => {
                                                          const status = fieldGroup._status || 'issued';
                                                          statusCounts[status] = (statusCounts[status] || 0) + 1;
                                                        });
                                                      }

                                                      const statusEntries = Object.entries(statusCounts);

                                                      if (statusEntries.length > 0) {
                                                        // Show summarized status badges
                                                        return (
                                                          <div className="flex flex-wrap gap-1">
                                                            {statusEntries.map(([status, count]) => (
                                                              <span
                                                                key={status}
                                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                  status === 'in_stock' ? 'bg-green-100 text-green-800' :
                                                                  status === 'issued' ? 'bg-yellow-100 text-yellow-800' :
                                                                  status === 'received' ? 'bg-brand-100 text-brand-800' :
                                                                  status === 'returned' ? 'bg-brand-100 text-brand-800' :
                                                                  'bg-steel-100 text-slate-800'
                                                                }`}
                                                              >
                                                                {status} ({count})
                                                              </span>
                                                            ))}
                                                          </div>
                                                        );
                                                      } else {
                                                        // No individual items - use API status field if available, fallback to quantity-based
                                                        const itemStatus = item.status || (quantityOutstanding === 0 ? 'returned' : 'issued');
                                                        return (
                                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            itemStatus === 'returned' ? 'bg-brand-100 text-brand-800' :
                                                            itemStatus === 'received' ? 'bg-brand-100 text-brand-800' :
                                                            itemStatus === 'in_stock' ? 'bg-green-100 text-green-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                          }`}>
                                                            {itemStatus}
                                                          </span>
                                                        );
                                                      }
                                                    })()}
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-slate-900">{quantityIssued}</td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className={quantityReturned > 0 ? 'text-green-600 font-medium' : 'text-slate-600'}>
                                                      {quantityReturned}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm">
                                                    <span className={quantityOutstanding > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                                                      {quantityOutstanding}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-sm text-slate-900">{formatPrice(unitPrice)}</td>
                                                  <td className="px-3 py-2 text-sm text-slate-900">{formatPrice(totalValue)}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No issue history found</h3>
              <p className="mt-1 text-sm text-slate-500">
                {issueHistory.length === 0 
                  ? 'Start by issuing items to a project to see the history here.'
                  : 'Try adjusting your search criteria or filters.'
                }
              </p>
            </div>
          )}
        </div>


        {/* Return Items Modal */}
        {showReturnModal && (
          <ProjectReturnModal
            projectData={returningProject}
            onReturn={handleReturnItems}
            onClose={() => {
              setShowReturnModal(false);
              setReturningProject(null);
            }}
          />
        )}

        {/* Transfer Project Modal */}
        {showTransferModal && (
          <ProjectTransferModal
            projectData={transferringProject}
            projects={projects.filter(p => p.id && p.project_name && p.id !== 'all' && p.id !== transferringProject?.projectId)}
            users={users}
            onTransfer={handleTransferProject}
            onClose={() => {
              setShowTransferModal(false);
              setTransferringProject(null);
            }}
          />
        )}

        {/* Car Responsible Person Change Modal */}
        {showCarToPersonModal && transferringCarData && (
          <CarToPersonTransferModal
            carData={transferringCarData}
            users={users}
            onTransfer={handleChangeCarResponsiblePerson}
            onClose={() => {
              setShowCarToPersonModal(false);
              setTransferringCarData(null);
            }}
          />
        )}

        {/* Car History Modal */}
        {showCarHistoryModal && carHistoryData && (
          <CarHistoryModal
            historyData={carHistoryData}
            onClose={() => {
              setShowCarHistoryModal(false);
              setCarHistoryData(null);
            }}
          />
        )}

        {/* Issue Detail Modal */}
        {showDetailModal && selectedIssue && (
          <IssueDetailModal
            issue={selectedIssue}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedIssue(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Project Return Modal Component
const ProjectReturnModal = ({ projectData, onReturn, onClose }) => {
  const [returnItems, setReturnItems] = useState([]);
  const [defaultCondition, setDefaultCondition] = useState('good');
  const [defaultNotes, setDefaultNotes] = useState('');
  const [bulkReturnQuantity, setBulkReturnQuantity] = useState(0);
  const [bulkItems, setBulkItems] = useState([]);
  const [selectedIndividualItems, setSelectedIndividualItems] = useState({});
  const [returnConditions, setReturnConditions] = useState({});
  const [returnNotes, setReturnNotes] = useState({});
  const [returnQuantities, setReturnQuantities] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (projectData) {
      console.log('ProjectReturnModal - Received projectData:', projectData);
      console.log('ProjectReturnModal - Type:', projectData.type);
      // Collect all outstanding items from all issues in this project
      const outstandingItems = [];
      projectData.issues.forEach(issue => {
        issue.items.forEach(item => {
          const quantityIssued = item.quantity_issued || 0;
          const quantityReturned = item.quantity_returned || 0;
          const outstanding = quantityIssued - quantityReturned;
          
          console.log(`Item ${item.item_name}: issued=${quantityIssued}, returned=${quantityReturned}, outstanding=${outstanding}`);
          
          if (outstanding > 0) {
            // Convert customFieldsSummary to individualItems if needed
            let individualItems = item.individual_items || [];

            // If no individual_items but we have custom_fields_summary, convert it
            if ((!individualItems || individualItems.length === 0) &&
                item.custom_fields_summary && item.custom_fields_summary.length > 0) {

              console.log(`Converting custom_fields_summary to individualItems for ${item.item_name}:`, item.custom_fields_summary);

              individualItems = item.custom_fields_summary.map((fieldGroup, idx) => {
                const individualItem = { custom_fields: {} };

                Object.entries(fieldGroup).forEach(([fieldName, fieldValue]) => {
                  if (fieldName.toLowerCase().includes('serial')) {
                    individualItem.serial_number = fieldValue;
                  } else {
                    individualItem.custom_fields[fieldName] = fieldValue;
                  }
                });

                return individualItem;
              });
              console.log(`Converted individualItems for ${item.item_name}:`, individualItems);
            }

            outstandingItems.push({
              issueItemId: item.id,
              issueNumber: issue.issueNumber,
              itemName: item.item_name,
              quantityIssued: item.quantity_issued,
              quantityReturned: item.quantity_returned || 0,
              quantityOutstanding: outstanding,
              unitPrice: parseFloat(item.unit_price_at_issue) || 0,
              quantityToReturn: 0,
              condition: 'good',
              notes: '',
              customFieldsSummary: item.custom_fields_summary || [],
              individualItems: individualItems
            });
          }
        });
      });
      
      console.log(`ProjectReturnModal - Found ${outstandingItems.length} items with outstanding quantity:`, outstandingItems);

      // Log individual items to check status
      outstandingItems.forEach(item => {
        if (item.individualItems && item.individualItems.length > 0) {
          console.log(`Individual items for ${item.itemName}:`, item.individualItems);
        }
        if (item.customFieldsSummary && item.customFieldsSummary.length > 0) {
          console.log(`Custom fields summary for ${item.itemName}:`, item.customFieldsSummary);
        }
      });

      setReturnItems(outstandingItems);

      // Create bulk items grouped by item name
      const itemGroups = {};
      outstandingItems.forEach(item => {
        if (!itemGroups[item.itemName]) {
          itemGroups[item.itemName] = {
            itemName: item.itemName,
            totalSent: 0,
            totalReturned: 0,
            totalOutstanding: 0,
            bulkReturnQuantity: 0
          };
        }
        itemGroups[item.itemName].totalSent += item.quantityIssued || 0;
        itemGroups[item.itemName].totalReturned += item.quantityReturned || 0;
        itemGroups[item.itemName].totalOutstanding += item.quantityOutstanding;
      });

      setBulkItems(Object.values(itemGroups));
    }
  }, [projectData]);

  const updateReturnItem = (index, field, value) => {
    setReturnItems(prev => {
      const newItems = prev.map((item, i) => {
        if (i === index) {
          // Validate quantity to return doesn't exceed outstanding
          if (field === 'quantityToReturn') {
            const maxReturn = item.quantityOutstanding;
            const validatedValue = Math.min(Math.max(0, parseInt(value) || 0), maxReturn);
            console.log(`Validating return for item ${item.itemName}: requested ${value}, outstanding ${maxReturn}, validated ${validatedValue}`);
            return { ...item, [field]: validatedValue };
          }
          return { ...item, [field]: value };
        }
        return item;
      });
      
      // Update bulk quantity when individual quantities change
      if (field === 'quantityToReturn') {
        const totalReturn = newItems.reduce((sum, item) => sum + (item.quantityToReturn || 0), 0);
        setBulkReturnQuantity(totalReturn);
      }
      
      return newItems;
    });
  };

  // Handle individual item selection
  const toggleIndividualItem = (itemIndex, individualItemIndex, checked) => {
    const itemKey = `${itemIndex}_${individualItemIndex}`;

    setSelectedIndividualItems(prev => {
      const newSelection = { ...prev };
      if (checked) {
        newSelection[itemKey] = true;
      } else {
        delete newSelection[itemKey];
      }

      // Update quantityToReturn based on selected individual items
      setReturnItems(prevItems => {
        const newItems = [...prevItems];
        const item = newItems[itemIndex];

        if (item.individualItems && item.individualItems.length > 0) {
          // Count selected individual items for this item
          const selectedCount = item.individualItems.reduce((count, _, idx) => {
            const key = `${itemIndex}_${idx}`;
            // Use the newSelection object to get accurate count
            return count + (newSelection[key] ? 1 : 0);
          }, 0);

          item.quantityToReturn = selectedCount;
        }

        return newItems;
      });

      return newSelection;
    });
  };

  const applyToAll = (field, value) => {
    setReturnItems(prev => prev.map(item => ({ ...item, [field]: value })));
  };

  const updateBulkItem = (itemName, quantity) => {
    setBulkItems(prev => prev.map(item =>
      item.itemName === itemName
        ? { ...item, bulkReturnQuantity: quantity }
        : item
    ));

    // Distribute quantity across assignments - fill first assignment, then second, etc.
    let remainingQuantity = quantity || 0;
    const newReturnQuantities = { ...returnQuantities };
    const newSelectedIndividualItems = { ...selectedIndividualItems };

    setReturnItems(prev => {
      const newItems = prev.map(item => ({ ...item })); // Deep copy

      // First pass: clear all selections for this item name and reset quantities
      newItems.forEach((item, itemIndex) => {
        if (item.itemName === itemName) {
          item.quantityToReturn = 0;
          newReturnQuantities[itemIndex] = 0;

          // Clear individual item selections
          const availableItems = item.individualItems || item.customFieldsSummary || [];
          availableItems.forEach((_, idx) => {
            delete newSelectedIndividualItems[`${itemIndex}_${idx}`];
          });
        }
      });

      // Reset remainingQuantity for second pass
      remainingQuantity = quantity || 0;

      // Second pass: distribute quantity across assignments
      newItems.forEach((item, itemIndex) => {
        if (item.itemName === itemName && remainingQuantity > 0) {
          const maxCanReturn = item.quantityOutstanding;
          const quantityForThisItem = Math.min(remainingQuantity, maxCanReturn);

          // Check if item has individual tracking
          const hasIndividualTracking = (item.individualItems && item.individualItems.length > 0 &&
            item.individualItems.some(indItem =>
              indItem.serial_number ||
              (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
            )) ||
            (item.customFieldsSummary && item.customFieldsSummary.length > 0);

          if (hasIndividualTracking) {
            // Auto-select individual items if this item has individual tracking
            const availableItems = item.individualItems || item.customFieldsSummary || [];

            // Select first N available (non-returned) items
            let selectedCount = 0;
            for (let idx = 0; idx < availableItems.length && selectedCount < quantityForThisItem; idx++) {
              const indItem = availableItems[idx];
              const isReturned = indItem.status === 'returned' ||
                                indItem.status === 'in_stock' ||
                                indItem.returned_at ||
                                indItem._retired === true;

              if (!isReturned) {
                newSelectedIndividualItems[`${itemIndex}_${idx}`] = true;
                selectedCount++;
              }
            }
          }

          item.quantityToReturn = quantityForThisItem;
          newReturnQuantities[itemIndex] = quantityForThisItem;
          remainingQuantity -= quantityForThisItem;
        }
      });

      return newItems;
    });

    // Update other states after setReturnItems
    setSelectedIndividualItems(newSelectedIndividualItems);
    setReturnQuantities(newReturnQuantities);
  };

  const handleQuantityChange = (index, value) => {
    const numValue = parseInt(value) || 0;

    setReturnQuantities(prev => ({
      ...prev,
      [index]: numValue
    }));

    // Also update quantityToReturn in returnItems to keep them in sync
    setReturnItems(prev => prev.map((item, idx) => {
      if (idx === index) {
        const maxCanReturn = item.quantityOutstanding;
        const validatedQuantity = Math.min(numValue, maxCanReturn);
        return { ...item, quantityToReturn: validatedQuantity };
      }
      return item;
    }));
  };

  const distributeBulkQuantity = (quantityPerItem) => {
    setBulkReturnQuantity(quantityPerItem);

    if (!quantityPerItem || quantityPerItem <= 0) {
      // Reset all quantities to 0 and clear all selections
      setReturnItems(prev => prev.map(item => ({ ...item, quantityToReturn: 0 })));
      setSelectedIndividualItems({});
      return;
    }

    setReturnItems(prev => {
      const newItems = [...prev];

      // Apply the same quantity to each item, but don't exceed outstanding quantity
      // Also auto-select individual items for items with individual tracking
      newItems.forEach((item, itemIndex) => {
        const maxCanReturn = item.quantityOutstanding;
        const validatedQuantity = Math.min(quantityPerItem, maxCanReturn);
        item.quantityToReturn = validatedQuantity;

        // Auto-select individual items if this item has individual tracking
        if ((item.individualItems && item.individualItems.length > 0) ||
            (item.customFieldsSummary && item.customFieldsSummary.length > 0)) {
          const availableItems = item.individualItems || item.customFieldsSummary || [];

          // Auto-select first N available (non-returned) items
          setSelectedIndividualItems(prevSelected => {
            const newSelected = { ...prevSelected };

            // First, clear any previous selections for this item
            availableItems.forEach((_, idx) => {
              delete newSelected[`${itemIndex}_${idx}`];
            });

            // Then select the first N available items
            let selectedCount = 0;
            for (let idx = 0; idx < availableItems.length && selectedCount < validatedQuantity; idx++) {
              const indItem = availableItems[idx];
              const isReturned = indItem.status === 'returned' ||
                                indItem.status === 'in_stock' ||
                                indItem.returned_at ||
                                indItem._retired === true;

              if (!isReturned) {
                newSelected[`${itemIndex}_${idx}`] = true;
                selectedCount++;
              }
            }

            return newSelected;
          });
        }
      });

      return newItems;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log('\n🚀 handleSubmit - Starting return process');
    console.log('📋 returnItems:', returnItems);
    console.log('✅ selectedIndividualItems:', selectedIndividualItems);

    const itemsToReturn = returnItems.filter(item => item.quantityToReturn > 0).map(item => {
      // Add selected individual items information
      const selectedIndividuals = [];
      const itemIndex = returnItems.indexOf(item);

      // Check individualItems first
      if (item.individualItems && item.individualItems.length > 0) {
        item.individualItems.forEach((individualItem, idx) => {
          const itemKey = `${itemIndex}_${idx}`;
          if (selectedIndividualItems[itemKey]) {
            selectedIndividuals.push({
              id: individualItem.id,  // Add id for backend
              index: idx,
              serial_number: individualItem.serial_number,
              custom_fields: individualItem.custom_fields,
              condition: item.condition,
              notes: item.notes
            });
          }
        });
      }
      // Fallback to customFieldsSummary
      else if (item.customFieldsSummary && item.customFieldsSummary.length > 0) {
        item.customFieldsSummary.forEach((fieldGroup, idx) => {
          const itemKey = `${itemIndex}_${idx}`;
          if (selectedIndividualItems[itemKey]) {
            // Convert fieldGroup to individual item format
            const customFields = {};
            let serialNumber = null;

            Object.entries(fieldGroup).forEach(([fieldName, fieldValue]) => {
              if (fieldName.toLowerCase().includes('serial')) {
                serialNumber = fieldValue;
              } else {
                customFields[fieldName] = fieldValue;
              }
            });

            selectedIndividuals.push({
              index: idx,
              serial_number: serialNumber,
              custom_fields: customFields,
              condition: item.condition,
              notes: item.notes
            });
          }
        });
      }

      const returnItem = {
        ...item,
        selectedIndividualItems: selectedIndividuals,
        hasIndividualSelection: selectedIndividuals.length > 0
      };

      console.log(`\n📦 Prepared return item for ${item.itemName}:`, {
        itemName: returnItem.itemName,
        quantityToReturn: returnItem.quantityToReturn,
        hasIndividualSelection: returnItem.hasIndividualSelection,
        selectedIndividualsCount: selectedIndividuals.length,
        individualItemsLength: item.individualItems?.length || 0,
        customFieldsSummaryLength: item.customFieldsSummary?.length || 0
      });

      return returnItem;
    });

    console.log('\n📊 itemsToReturn:', itemsToReturn);

    if (itemsToReturn.length === 0) {
      alert('Please enter quantities for items to return');
      return;
    }

    // Validate quantities
    for (const item of itemsToReturn) {
      if (item.quantityToReturn > item.quantityOutstanding) {
        alert(`Cannot return more than ${item.quantityOutstanding} of ${item.itemName}`);
        return;
      }

      // Validate individual item selection for items with individual tracking (but only if they have actual data)
      const hasIndividualItemsWithData = (item.individualItems && item.individualItems.length > 0 &&
        item.individualItems.some(indItem =>
          indItem.serial_number ||
          (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
        )) ||
        (item.customFieldsSummary && item.customFieldsSummary.length > 0);

      if (hasIndividualItemsWithData) {
        const selectedCount = item.selectedIndividualItems ? item.selectedIndividualItems.length : 0;
        if (item.quantityToReturn > 0 && selectedCount !== item.quantityToReturn) {
          alert(
            `❌ Invalid selection for "${item.itemName}":\n\n` +
            `This item has individual tracking (serial numbers/custom fields).\n` +
            `You have set quantity to return: ${item.quantityToReturn}\n` +
            `But you have selected: ${selectedCount} individual items\n\n` +
            `Please use the checkboxes in the "Additional Fields" column to select exactly ${item.quantityToReturn} item(s) to return.`
          );
          return;
        }
      }
    }

    onReturn({
      projectId: projectData.projectId,
      type: projectData.type, // Pass type so we know if it's car/project/person
      returns: itemsToReturn
    });
  };

  const totalToReturn = returnItems.reduce((sum, item) => sum + item.quantityToReturn, 0);

  // Filter items based on search query
  const filteredBulkItems = bulkItems.filter(item =>
    item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredReturnItems = returnItems.filter(item =>
    item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Return Items to Warehouse</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="mb-4 p-3 bg-brand-50 rounded-xl">
            <p className="text-sm text-brand-800">
              <strong>Project:</strong> {projectData?.projectName}
            </p>
            <p className="text-xs text-brand-600 mt-1">
              Total Outstanding Items: {returnItems.reduce((sum, item) => sum + item.quantityOutstanding, 0)}
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search items by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 pl-10 text-sm border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-3.5 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Bulk Return Controls */}
          <div className="mb-4 p-4 bg-gradient-to-r from-brand-50 to-brand-100 rounded-xl border border-brand-200">
            <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
              Bulk Return by Item Type
            </h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-steel-100 border-b-2 border-brand-500">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Item Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Total Sent</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Returned</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Outstanding</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Return Qty</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {filteredBulkItems.map((item, index) => (
                    <tr key={index} className="hover:bg-brand-50 transition-colors">
                      <td className="px-3 py-2 text-sm font-medium text-slate-900">{item.itemName}</td>
                      <td className="px-3 py-2 text-sm text-slate-900">{item.totalSent}</td>
                      <td className="px-3 py-2 text-sm text-slate-900">{item.totalReturned}</td>
                      <td className="px-3 py-2 text-sm text-slate-900">{item.totalOutstanding}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            max={item.totalOutstanding}
                            value={item.bulkReturnQuantity || ''}
                            onChange={(e) => updateBulkItem(item.itemName, parseInt(e.target.value) || 0)}
                            className={`w-20 p-1 text-sm border rounded ${
                              item.bulkReturnQuantity > 0 
                                ? 'border-brand-500 bg-brand-50' 
                                : 'border-brand-200'
                            }`}
                            placeholder="0"
                          />
                          <button
                            type="button"
                            onClick={() => updateBulkItem(item.itemName, item.totalOutstanding)}
                            className="px-2 py-1 text-xs bg-brand-100 text-brand-700 border border-brand-500 rounded-xl hover:bg-brand-200 transition-colors"
                            title="Return all outstanding for this item"
                          >
                            All
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Global Controls */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Default Condition</label>
                <select
                  value={defaultCondition}
                  onChange={(e) => {
                    setDefaultCondition(e.target.value);
                    applyToAll('condition', e.target.value);
                  }}
                  className="w-full p-2 text-sm border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="good">Good - Return to stock</option>
                  <option value="damaged">Damaged - Do not return to stock</option>
                  <option value="lost">Lost - Write off</option>
                  <option value="consumed">Consumed - Write off</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Default Notes</label>
                <input
                  type="text"
                  value={defaultNotes}
                  onChange={(e) => {
                    setDefaultNotes(e.target.value);
                    applyToAll('notes', e.target.value);
                  }}
                  className="w-full p-2 text-sm border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Return notes for all items..."
                />
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Instructions */}
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-brand-700">
                <strong>Individual Item Selection:</strong> For items with individual tracking (serial numbers, custom fields),
                use the checkboxes in the "Additional Fields" column to select exactly which items to return.
                The "Return Qty" will automatically update based on your selections.
              </p>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-steel-100 border-b-2 border-brand-500">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Issue #</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Item</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Additional Fields</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Outstanding</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Return Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Condition</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {filteredReturnItems.flatMap((item, filteredIndex) => {
                    // Get original item index for state management
                    const itemIndex = returnItems.indexOf(item);

                    // Check if item has individual items WITH actual data (serial numbers or custom fields)
                    const hasIndividualItemsWithData = item.individualItems &&
                      item.individualItems.length > 0 &&
                      item.individualItems.some(indItem =>
                        indItem.serial_number ||
                        (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                      );

                    // Create rows for individual items (only those with meaningful data)
                    if (hasIndividualItemsWithData) {
                      // Filter to only show items with serial number or custom fields
                      const itemsWithData = item.individualItems.filter(indItem =>
                        indItem.serial_number ||
                        (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                      );

                      if (itemsWithData.length > 0) {
                        return itemsWithData.map((individualItem, idx) => {
                          const itemKey = `${itemIndex}_${idx}`;
                          const isSelected = selectedIndividualItems[itemKey] || false;
                          const isReturned = individualItem.status === 'returned' || individualItem.status === 'in_stock' || individualItem.returned_at;

                          return (
                          <tr key={`${itemIndex}_${idx}`} className={`hover:bg-brand-50 transition-colors ${isReturned ? 'bg-steel-100' : ''}`}>
                            <td className="px-3 py-2 text-xs text-slate-600">{item.issueNumber}</td>
                            <td className="px-3 py-2 text-sm text-slate-900">{item.itemName}</td>
                            <td className="px-3 py-2 text-xs">
                              <div className={`p-2 rounded border ${
                                isReturned ? 'border-brand-200 bg-steel-50' :
                                isSelected ? 'border-green-400 bg-green-50' : 'border-brand-200 bg-brand-50'
                              }`}>
                                <div className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => toggleIndividualItem(itemIndex, idx, e.target.checked)}
                                    className="mt-1 h-3 w-3 text-brand-600"
                                    disabled={isReturned}
                                  />
                                  <div className="flex-1">
                                    {isReturned && (
                                      <div className="mb-1">
                                        <span className="inline-block px-2 py-1 text-xs font-medium bg-steel-500 text-white rounded">
                                          RETURNED
                                        </span>
                                      </div>
                                    )}
                                    {individualItem.serial_number && (
                                      <div>
                                        <span className="font-medium text-slate-600">Serial:</span>
                                        <span className={`ml-1 ${isReturned ? "text-slate-500 line-through" : "text-brand-600"}`}>
                                          {individualItem.serial_number}
                                        </span>
                                      </div>
                                    )}
                                    {individualItem.custom_fields && Object.keys(individualItem.custom_fields).length > 0 && (
                                      <div className="mt-1">
                                        {Object.entries(individualItem.custom_fields).map(([fieldName, fieldValue]) => {
                                          // Extract just the field name from patterns like "Power_item_1758863981636_w0g2373zo"
                                          const cleanFieldName = fieldName.split('_')[0];
                                          return (
                                            <div key={fieldName}>
                                              <span className="font-medium text-slate-600">
                                                {cleanFieldName}:
                                              </span>
                                              <span className={`ml-1 ${isReturned ? "text-slate-500 line-through" : "text-brand-600"}`}>
                                                {fieldValue}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-slate-900">1</td>
                            <td className="px-3 py-2">
                              <span className={`w-16 p-1 text-sm border rounded text-center inline-block ${
                                isSelected && !isReturned
                                  ? 'border-green-300 bg-green-50 text-green-800'
                                  : isReturned
                                  ? 'border-brand-200 bg-steel-50 text-slate-600'
                                  : 'border-brand-200 bg-steel-50 text-slate-600'
                              }`}>
                                {isSelected && !isReturned ? '1' : '0'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={returnConditions[`${itemIndex}_${idx}`] || 'good'}
                                onChange={(e) => setReturnConditions({...returnConditions, [`${itemIndex}_${idx}`]: e.target.value})}
                                className="text-sm border border-brand-200 rounded px-2 py-1"
                                disabled={isReturned}
                              >
                                <option value="good">Good</option>
                                <option value="damaged">Damaged</option>
                                <option value="lost">Lost</option>
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                placeholder="Notes..."
                                value={returnNotes[`${itemIndex}_${idx}`] || ''}
                                onChange={(e) => setReturnNotes({...returnNotes, [`${itemIndex}_${idx}`]: e.target.value})}
                                className="w-full text-sm border border-brand-200 rounded px-2 py-1"
                                disabled={isReturned}
                              />
                            </td>
                          </tr>
                          );
                        });
                      }
                    }

                    // Create rows for custom fields summary
                    if (item.customFieldsSummary && item.customFieldsSummary.length > 0) {
                      return item.customFieldsSummary.map((fieldGroup, groupIdx) => {
                        const itemKey = `${itemIndex}_${groupIdx}`;
                        const isSelected = selectedIndividualItems[itemKey] || false;
                        const isReturned = fieldGroup.status === 'returned' || fieldGroup.status === 'in_stock' || fieldGroup.returned_at;

                        return (
                          <tr key={`${itemIndex}_${groupIdx}`} className={`hover:bg-brand-50 transition-colors ${isReturned ? 'bg-steel-100' : ''}`}>
                            <td className="px-3 py-2 text-xs text-slate-600">{item.issueNumber}</td>
                            <td className="px-3 py-2 text-sm text-slate-900">{item.itemName}</td>
                            <td className="px-3 py-2 text-xs">
                              <div className={`p-2 rounded border ${
                                isReturned ? 'border-brand-200 bg-steel-50' :
                                isSelected ? 'border-green-400 bg-green-50' : 'border-brand-200 bg-brand-50'
                              }`}>
                                <div className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => toggleIndividualItem(itemIndex, groupIdx, e.target.checked)}
                                    className="mt-1 h-3 w-3 text-brand-600"
                                    disabled={isReturned}
                                  />
                                  <div className="flex-1">
                                    {isReturned && (
                                      <div className="mb-1">
                                        <span className="inline-block px-2 py-1 text-xs font-medium bg-steel-500 text-white rounded">
                                          RETURNED
                                        </span>
                                      </div>
                                    )}
                                    {Object.entries(fieldGroup).map(([fieldName, fieldValue], idx) => {
                                      // Skip status and returned_at fields
                                      if (fieldName === 'status' || fieldName === 'returned_at') return null;
                                      // Extract just the field name from patterns like "Power_item_1758863981636_w0g2373zo"
                                      const cleanFieldName = fieldName.split('_')[0];
                                      return (
                                        <div key={idx}>
                                          <span className="font-medium text-slate-600">{cleanFieldName}:</span>
                                          <span className={`ml-1 ${isReturned ? "text-slate-500 line-through" : "text-brand-600"}`}>
                                            {fieldValue}
                                          </span>
                                        </div>
                                      );
                                    }).filter(Boolean)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-slate-900">1</td>
                            <td className="px-3 py-2">
                              <span className={`w-16 p-1 text-sm border rounded text-center inline-block ${
                                isSelected && !isReturned
                                  ? 'border-green-300 bg-green-50 text-green-800'
                                  : isReturned
                                  ? 'border-brand-200 bg-steel-50 text-slate-600'
                                  : 'border-brand-200 bg-steel-50 text-slate-600'
                              }`}>
                                {isSelected && !isReturned ? '1' : '0'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={returnConditions[`${itemIndex}_${groupIdx}`] || 'good'}
                                onChange={(e) => setReturnConditions({...returnConditions, [`${itemIndex}_${groupIdx}`]: e.target.value})}
                                className="text-sm border border-brand-200 rounded px-2 py-1"
                                disabled={isReturned}
                              >
                                <option value="good">Good</option>
                                <option value="damaged">Damaged</option>
                                <option value="lost">Lost</option>
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                placeholder="Notes..."
                                value={returnNotes[`${itemIndex}_${groupIdx}`] || ''}
                                onChange={(e) => setReturnNotes({...returnNotes, [`${itemIndex}_${groupIdx}`]: e.target.value})}
                                className="w-full text-sm border border-brand-200 rounded px-2 py-1"
                                disabled={isReturned}
                              />
                            </td>
                          </tr>
                        );
                      });
                    }

                    // Fallback for items without individual items or custom fields
                    return [(
                      <tr key={itemIndex} className="hover:bg-brand-50 transition-colors">
                        <td className="px-3 py-2 text-xs text-slate-600">{item.issueNumber}</td>
                        <td className="px-3 py-2 text-sm text-slate-900">{item.itemName}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className="text-slate-400">No additional fields</span>
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-900">{item.quantityOutstanding}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={returnQuantities[itemIndex] || ''}
                            onChange={(e) => handleQuantityChange(itemIndex, e.target.value)}
                            className="w-20 p-1 text-sm border border-brand-200 rounded"
                            min="0"
                            max={item.quantityOutstanding}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={returnConditions[itemIndex] || 'good'}
                            onChange={(e) => setReturnConditions({...returnConditions, [itemIndex]: e.target.value})}
                            className="text-sm border border-brand-200 rounded px-2 py-1"
                          >
                            <option value="good">Good</option>
                            <option value="damaged">Damaged</option>
                            <option value="lost">Lost</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            placeholder="Notes..."
                            value={returnNotes[itemIndex] || ''}
                            onChange={(e) => setReturnNotes({...returnNotes, [itemIndex]: e.target.value})}
                            className="w-full text-sm border border-brand-200 rounded px-2 py-1"
                          />
                        </td>
                      </tr>
                    )];
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="bg-steel-50 rounded-xl p-3">
              <h4 className="font-medium text-slate-900 mb-2">Return Summary</h4>
              <p className="text-sm text-slate-600">
                Total Outstanding Items: {returnItems.reduce((sum, item) => sum + item.quantityOutstanding, 0)}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-brand-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-700 border border-brand-200 rounded-xl hover:bg-brand-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
              >
                Process Return
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Issue Detail Modal Component
const IssueDetailModal = ({ issue, onClose }) => {
  const totalIssued = issue.items.reduce((sum, item) => sum + (item.quantity_issued || 0), 0);
  const totalReturned = issue.items.reduce((sum, item) => sum + (item.quantity_returned || 0), 0);
  const totalOutstanding = issue.items.reduce((sum, item) => sum + (item.quantity_issued - item.quantity_returned || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Issue Details</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="bg-steel-50 rounded-xl p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Project</p>
                <p className="font-medium">{issue.projectName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Issue Date</p>
                <p className="font-medium">{new Date(issue.issued_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Status</p>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${{
                  'active': 'bg-green-100 text-green-800',
                  'partially_returned': 'bg-yellow-100 text-yellow-800',
                  'fully_returned': 'bg-brand-100 text-brand-800',
                  'cancelled': 'bg-red-100 text-red-800'
                }[issue.status] || 'bg-steel-100 text-slate-800'}`}>
                  {issue.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Value</p>
                <p className="font-medium">{formatPrice(issue.totalValue)}</p>
              </div>
              {issue.shipmentName && (
                <div>
                  <p className="text-sm text-slate-600">Shipment</p>
                  <p className="font-medium">{issue.shipmentName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-brand-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-brand-600">{totalIssued}</p>
              <p className="text-sm text-slate-600">Total Issued</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{totalReturned}</p>
              <p className="text-sm text-slate-600">Total Returned</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">{totalOutstanding}</p>
              <p className="text-sm text-slate-600">Outstanding</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-steel-100 border-b-2 border-brand-500">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Individual Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Issued</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Returned</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Outstanding</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Total Value</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {issue.items.map((item, index) => {
                    const quantityIssued = item.quantity_issued || 0;
                    const quantityReturned = item.quantity_returned || 0;
                    const quantityOutstanding = quantityIssued - quantityReturned;
                    const unitPrice = parseFloat(item.unit_price_at_issue) || 0;
                    const totalValue = quantityIssued * unitPrice;

                    return (
                      <tr key={index} className="hover:bg-brand-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-900">{item.item_name || 'Unknown Item'}</td>
                        <td className="px-4 py-2 text-xs">
                          {item.individual_items && item.individual_items.length > 0 ? (
                            <div className="space-y-1">
                              {item.individual_items.map((indItem, idx) => (
                                <div
                                  key={idx}
                                  className={`text-xs p-2 rounded border ${
                                    indItem.retired
                                      ? 'bg-red-50 border-red-300'
                                      : 'bg-steel-50 border-brand-200'
                                  }`}
                                >
                                  {indItem.retired && (
                                    <div className="text-red-600 font-semibold mb-1">
                                      🚫 RETIRED
                                    </div>
                                  )}
                                  {indItem.serial_number && (
                                    <div>
                                      <span className="font-medium">Serial:</span> {indItem.serial_number}
                                    </div>
                                  )}
                                  {indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0 && (
                                    <div>
                                      {Object.entries(indItem.custom_fields).map(([key, value]) => {
                                        // Clean up the field name by removing _item_ suffix
                                        const cleanKey = key.replace(/_item_[a-z0-9]+$/i, '');
                                        return (
                                          <div key={key}>
                                            <span className="font-medium">{cleanKey}:</span> {value}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">No details</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">{quantityIssued}</td>
                        <td className="px-4 py-3 text-sm text-green-600">{quantityReturned}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <span className={quantityOutstanding > 0 ? 'text-orange-600' : 'text-green-600'}>
                            {quantityOutstanding}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">{formatPrice(unitPrice)}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{formatPrice(totalValue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-steel-500 text-white rounded-xl hover:bg-steel-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Project Transfer Modal Component
const ProjectTransferModal = ({ projectData, projects, users, onTransfer, onClose }) => {
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [itemTargetProjects, setItemTargetProjects] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');


  const handlePersonToggle = (personId) => {
    setSelectedPersons(prev =>
      prev.includes(personId)
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  const handleItemToggle = (itemKey) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
        // Remove from target projects when deselected
        setItemTargetProjects(prev => {
          const newTargets = { ...prev };
          delete newTargets[itemKey];
          return newTargets;
        });
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const handleItemTargetProjectChange = (itemKey, projectId) => {
    setItemTargetProjects(prev => ({
      ...prev,
      [itemKey]: projectId
    }));
  };

  const handleSelectAllItems = () => {
    const allItems = [];
    (projectData?.issues || []).forEach(issue => {
      (issue.items || []).forEach((item, itemIndex) => {
        const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
        if (quantityOutstanding > 0) {
          // If item has individual items with actual data, add each individual item
          if (item.individual_items && item.individual_items.length > 0 &&
              item.individual_items.some(indItem =>
                indItem.serial_number ||
                (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
              )) {
            item.individual_items.forEach((indItem, idx) => {
              allItems.push(`${issue.id}-${item.id}-${itemIndex}-${idx}`);
            });
          } else {
            // For bulk items, add the item key
            allItems.push(`${issue.id}-${item.id}-${itemIndex}`);
          }
        }
      });
    });

    if (selectedItems.size === allItems.length) {
      setSelectedItems(new Set());
      setItemTargetProjects({});
    } else {
      setSelectedItems(new Set(allItems));
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchMatch = !userSearchQuery ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearchQuery.toLowerCase());
    return searchMatch;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Group items by target project
      const itemsByTargetProject = {};

      selectedItems.forEach(itemKey => {
        const targetProjectId = itemTargetProjects[itemKey];
        if (targetProjectId) {
          if (!itemsByTargetProject[targetProjectId]) {
            itemsByTargetProject[targetProjectId] = [];
          }
          itemsByTargetProject[targetProjectId].push(itemKey);
        }
      });

      // Create transfer data with grouped items
      for (const [targetProjectId, itemKeys] of Object.entries(itemsByTargetProject)) {
        const filteredIssues = (projectData?.issues || []).map(issue => ({
          ...issue,
          items: (issue.items || []).map((item, itemIndex) => {
            const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
            if (quantityOutstanding <= 0) return null;

            // Check if this item has any selected individual items
            const selectedIndividualItems = [];

            // Check if item has individual items with actual data (not just empty objects)
            if (item.individual_items && item.individual_items.length > 0 &&
                item.individual_items.some(indItem =>
                  indItem.serial_number ||
                  (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                )) {
              // Process individual items
              item.individual_items.forEach((indItem, idx) => {
                const itemKey = `${issue.id}-${item.id}-${itemIndex}-${idx}`;
                if (itemKeys.includes(itemKey)) {
                  selectedIndividualItems.push(indItem);
                }
              });

              // If we have selected individual items, create a new item with only those
              if (selectedIndividualItems.length > 0) {
                return {
                  ...item,
                  individual_items: selectedIndividualItems,
                  quantity_issued: selectedIndividualItems.length,
                  quantity_returned: 0
                };
              }
            } else {
              // Handle bulk items (items without meaningful individual tracking)
              const itemKey = `${issue.id}-${item.id}-${itemIndex}`;
              if (itemKeys.includes(itemKey)) {
                return item; // Return the whole item
              }
            }

            return null;
          }).filter(item => item !== null)
        })).filter(issue => issue.items.length > 0);

        const transferData = {
          ...projectData,
          issues: filteredIssues
        };

        return onTransfer({
          sourceProjectData: transferData,
          targetProjectId: targetProjectId,
          responsiblePersonIds: selectedPersons,
          notes: `Transfer of selected items from ${projectData?.projectName || 'project'}`
        });
      }
    } catch (error) {
      console.error('Error in transfer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-2xl bg-white max-h-[95vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Transfer Items</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column - Person Selection */}
              <div className="col-span-4">
                <div>
                  <label className="block font-medium text-slate-700 mb-2">
                    Select Person/People (Optional)
                  </label>
                  <div className="border border-brand-200 rounded-xl max-h-40 overflow-hidden">
                    {/* Search Input - sticky at top */}
                    <div className="sticky top-0 bg-white border-b border-brand-200 p-2">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-brand-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    {/* Users list */}
                    <div className="max-h-32 overflow-y-auto">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                          <label key={user.id} className="flex items-center px-3 py-2 hover:bg-brand-50 transition-colors cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPersons.includes(`person_${user.id}`)}
                              onChange={() => handlePersonToggle(`person_${user.id}`)}
                              className="mr-2"
                            />
                            <span className="text-sm">{user.first_name} {user.last_name}</span>
                          </label>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-slate-500 italic">
                          {userSearchQuery ? 'No users found' : 'No users available'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Item Selection */}
              <div className="col-span-8">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block font-medium text-slate-700">
                      Select Items to Transfer
                    </label>
                    <button
                      type="button"
                      onClick={handleSelectAllItems}
                      className="text-sm text-brand-600 hover:text-brand-800"
                    >
                      {selectedItems.size > 0 ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="border border-brand-200 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-steel-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Select</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Item</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Additional Fields</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Outstanding</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Target Project</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {(() => {
                            // First, collect all outstanding items
                            const allItems = [];
                            const groupedBulkItems = new Map(); // Group items without individual tracking

                            (projectData?.issues || []).forEach(issue => {
                              (issue.items || []).forEach((item, itemIndex) => {
                                const quantityIssued = item.quantity_issued || 0;
                                const quantityReturned = item.quantity_returned || 0;
                                const quantityOutstanding = quantityIssued - quantityReturned;

                                if (quantityOutstanding > 0) {
                                  // If item has individual items with actual data, create a row for each
                                  if (item.individual_items && item.individual_items.length > 0 &&
                                      item.individual_items.some(indItem =>
                                        indItem.serial_number ||
                                        (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                                      )) {
                                    item.individual_items.forEach((indItem, idx) => {
                                      const itemKey = `${issue.id}-${item.id}-${itemIndex}-${idx}`;

                                      // Prepare additional fields display
                                      const additionalFields = [];
                                      if (indItem.serial_number) {
                                        additionalFields.push(`Serial: ${indItem.serial_number}`);
                                      }
                                      if (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0) {
                                        Object.entries(indItem.custom_fields).forEach(([key, value]) => {
                                          // Extract just the field name from patterns like "Power_item_1758863981636_w0g2373zo"
                                          const cleanKey = key.split('_')[0];
                                          additionalFields.push(`${cleanKey}: ${value}`);
                                        });
                                      }

                                      allItems.push(
                                        <tr key={itemKey} className="hover:bg-brand-50 transition-colors">
                                          <td className="px-3 py-2">
                                            <input
                                              type="checkbox"
                                              checked={selectedItems.has(itemKey)}
                                              onChange={() => handleItemToggle(itemKey)}
                                              className="text-brand-600"
                                            />
                                          </td>
                                          <td className="px-3 py-2 text-sm text-slate-900">{item.item_name}</td>
                                          <td className="px-3 py-2 text-xs">
                                            <div className="text-xs bg-steel-50 p-1 rounded">
                                              {additionalFields.length > 0 ? (
                                                additionalFields.join(' | ')
                                              ) : (
                                                <span className="text-slate-400">No details</span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 text-sm font-medium text-orange-600">
                                            1
                                          </td>
                                          <td className="px-3 py-2">
                                            <select
                                              value={itemTargetProjects[itemKey] || ''}
                                              onChange={(e) => handleItemTargetProjectChange(itemKey, e.target.value)}
                                              className="text-sm border border-brand-200 rounded px-2 py-1 w-full"
                                              disabled={!selectedItems.has(itemKey)}
                                            >
                                              <option value="">Select project...</option>
                                              {(projects || [])
                                                .filter(project => project.id !== projectData?.projectId && project.id !== 'all' && project.is_active !== false)
                                                .map(project => (
                                                  <option key={project.id} value={project.id}>
                                                    {project.project_name}
                                                  </option>
                                                ))
                                              }
                                            </select>
                                          </td>
                                        </tr>
                                      );
                                    });
                                  } else {
                                    // Group items without individual tracking by item name
                                    const groupKey = item.item_name;
                                    if (groupedBulkItems.has(groupKey)) {
                                      const existing = groupedBulkItems.get(groupKey);
                                      existing.totalQuantity += quantityOutstanding;
                                      existing.itemKeys.push(`${issue.id}-${item.id}-${itemIndex}`);
                                      existing.items.push({ issue, item, itemIndex, quantityOutstanding });
                                    } else {
                                      groupedBulkItems.set(groupKey, {
                                        itemName: item.item_name,
                                        totalQuantity: quantityOutstanding,
                                        itemKeys: [`${issue.id}-${item.id}-${itemIndex}`],
                                        items: [{ issue, item, itemIndex, quantityOutstanding }]
                                      });
                                    }
                                  }
                                }
                              });
                            });

                            // Add grouped bulk items
                            Array.from(groupedBulkItems.values()).forEach(groupedItem => {
                              const groupKey = `group_${groupedItem.itemName.replace(/\s+/g, '_')}`;

                              allItems.push(
                                <tr key={groupKey} className="hover:bg-brand-50 transition-colors">
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={groupedItem.itemKeys.every(key => selectedItems.has(key))}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        groupedItem.itemKeys.forEach(key => {
                                          if (isChecked && !selectedItems.has(key)) {
                                            handleItemToggle(key);
                                          } else if (!isChecked && selectedItems.has(key)) {
                                            handleItemToggle(key);
                                          }
                                        });
                                      }}
                                      className="text-brand-600"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-900">{groupedItem.itemName}</td>
                                  <td className="px-3 py-2 text-xs">
                                    <span className="text-slate-400">No additional fields</span>
                                  </td>
                                  <td className="px-3 py-2 text-sm font-medium text-orange-600">
                                    {groupedItem.totalQuantity}
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={groupedItem.itemKeys.find(key => itemTargetProjects[key]) ?
                                             itemTargetProjects[groupedItem.itemKeys.find(key => itemTargetProjects[key])] : ''}
                                      onChange={(e) => {
                                        // Apply same project to all items in group
                                        groupedItem.itemKeys.forEach(key => {
                                          handleItemTargetProjectChange(key, e.target.value);
                                        });
                                      }}
                                      className="text-sm border border-brand-200 rounded px-2 py-1 w-full"
                                      disabled={!groupedItem.itemKeys.some(key => selectedItems.has(key))}
                                    >
                                      <option value="">Select project...</option>
                                      {(projects || [])
                                        .filter(project => project.id !== projectData?.projectId && project.id !== 'all' && project.is_active !== false)
                                        .map(project => (
                                          <option key={project.id} value={project.id}>
                                            {project.project_name}
                                          </option>
                                        ))
                                      }
                                    </select>
                                  </td>
                                </tr>
                              );
                            });

                            return allItems;
                          })()}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary Row */}
                    <div className="bg-brand-50 border-t border-brand-200 px-4 py-3">
                      <div className="text-sm font-medium text-brand-900 mb-2">
                        Transfer Summary
                      </div>

                      {selectedItems.size > 0 ? (
                        <div className="space-y-2">
                          {/* Group selected items by target project */}
                          {(() => {
                            const projectGroups = {};
                            const itemsData = [];

                            // Collect selected items data
                            (projectData?.issues || []).forEach(issue => {
                              (issue.items || []).forEach((item, itemIndex) => {
                                const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
                                if (quantityOutstanding > 0) {
                                  if (item.individual_items && item.individual_items.length > 0 &&
                                      item.individual_items.some(indItem =>
                                        indItem.serial_number ||
                                        (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0)
                                      )) {
                                    item.individual_items.forEach((indItem, idx) => {
                                      const itemKey = `${issue.id}-${item.id}-${itemIndex}-${idx}`;
                                      if (selectedItems.has(itemKey)) {
                                        const additionalFields = [];
                                        if (indItem.serial_number) {
                                          additionalFields.push(`Serial: ${indItem.serial_number}`);
                                        }
                                        if (indItem.custom_fields && Object.keys(indItem.custom_fields).length > 0) {
                                          Object.entries(indItem.custom_fields).forEach(([key, value]) => {
                                            const cleanKey = key.split('_')[0];
                                            additionalFields.push(`${cleanKey}: ${value}`);
                                          });
                                        }
                                        itemsData.push({
                                          key: itemKey,
                                          name: item.item_name,
                                          details: additionalFields.join(' | ') || 'No details',
                                          quantity: 1
                                        });
                                      }
                                    });
                                  } else {
                                    // Group bulk items
                                    const itemKey = `${issue.id}-${item.id}-${itemIndex}`;
                                    if (selectedItems.has(itemKey)) {
                                      itemsData.push({
                                        key: itemKey,
                                        name: item.item_name,
                                        details: 'No additional fields',
                                        quantity: quantityOutstanding
                                      });
                                    }
                                  }
                                }
                              });
                            });

                            // Group by target project
                            itemsData.forEach(item => {
                              const targetProjectId = itemTargetProjects[item.key];
                              if (targetProjectId) {
                                const project = projects.find(p => p.id.toString() === targetProjectId.toString());
                                const projectName = project ? project.project_name : `Project ${targetProjectId}`;

                                if (!projectGroups[projectName]) {
                                  projectGroups[projectName] = [];
                                }
                                projectGroups[projectName].push(item);
                              }
                            });

                            return Object.entries(projectGroups).map(([projectName, items]) => (
                              <div key={projectName} className="text-xs">
                                <div className="font-medium text-brand-800">→ {projectName}:</div>
                                <div className="ml-4 space-y-1">
                                  {items.map((item, idx) => (
                                    <div key={idx} className="text-slate-700">
                                      • {item.name} {item.quantity > 1 ? `(${item.quantity})` : ''}
                                      {item.details !== 'No additional fields' && (
                                        <span className="text-slate-500"> - {item.details}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}

                          <div className="text-xs text-brand-700 pt-2 border-t border-brand-200">
                            Total: {selectedItems.size} selected items → {Object.keys(itemTargetProjects).length} projects
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-600">
                          No items selected for transfer
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Select items to transfer and choose target projects for each item.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-brand-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-700 border border-brand-200 rounded-xl hover:bg-brand-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedItems.size === 0 || isLoading}
                className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 disabled:bg-steel-400"
              >
                {isLoading ? 'Processing...' : 'Transfer Items'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Car Responsible Person Change Modal Component
const CarToPersonTransferModal = ({ carData, users, onTransfer, onClose }) => {
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [selectedPerson2Id, setSelectedPerson2Id] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSecondPerson, setShowSecondPerson] = useState(false);

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || email.includes(searchQuery.toLowerCase());
  });

  // Get outstanding items from car
  const outstandingItems = [];
  (carData?.issues || []).forEach(issue => {
    (issue.items || []).forEach(item => {
      const quantityOutstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
      if (quantityOutstanding > 0) {
        outstandingItems.push({
          ...item,
          quantityOutstanding
        });
      }
    });
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPersonId) {
      alert('Please select a responsible person.');
      return;
    }

    if (outstandingItems.length === 0) {
      alert('No items in car.');
      return;
    }

    setIsLoading(true);

    try {
      await onTransfer({
        carData,
        targetPersonId: parseInt(selectedPersonId),
        targetPerson2Id: selectedPerson2Id ? parseInt(selectedPerson2Id) : null,
        notes
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedPersonName = (id) => {
    const user = users.find(u => u.id.toString() === id);
    return user ? `${user.first_name} ${user.last_name}` : '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-11/12 lg:w-4/5 xl:w-3/4 max-w-5xl shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Change Responsible Person
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Car Info */}
          <div className="mb-4 p-3 bg-brand-50 rounded-xl">
            <p className="text-sm text-brand-800">
              <strong>🚗 Car:</strong> {carData?.car_info || carData?.projectName}
            </p>
            {carData?.responsiblePersons && carData.responsiblePersons.length > 0 && (
              <p className="text-xs text-brand-600 mt-1">
                <strong>Current responsible:</strong> {carData.responsiblePersons.join(', ')}
              </p>
            )}
          </div>

          {/* Two Column Layout */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left Column - Form */}
            <div className="lg:w-1/2">
              <form onSubmit={handleSubmit}>
                {/* Primary Person Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    👤 New Responsible Person <span className="text-red-500">*</span>
                  </label>

                  {/* Search */}
                  <div className="mb-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search person by name or email..."
                      className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500"
                    />
                  </div>

                  {/* Person List */}
                  <div className="max-h-40 overflow-y-auto border rounded-xl">
                    {filteredUsers.length === 0 ? (
                      <div className="px-3 py-4 text-center text-slate-500 text-sm">
                        No users found
                      </div>
                    ) : (
                      filteredUsers.map(user => (
                        <div
                          key={user.id}
                          onClick={() => setSelectedPersonId(user.id.toString())}
                          className={`px-3 py-2 border-b last:border-b-0 cursor-pointer transition-colors ${
                            selectedPersonId === user.id.toString()
                              ? 'bg-brand-100 border-brand-500'
                              : 'hover:bg-brand-50 transition-colors'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              checked={selectedPersonId === user.id.toString()}
                              onChange={() => setSelectedPersonId(user.id.toString())}
                              className="mr-3"
                            />
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-xs text-slate-500">{user.email}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Second Person Toggle */}
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSecondPerson(!showSecondPerson);
                      if (showSecondPerson) setSelectedPerson2Id('');
                    }}
                    className="text-sm text-brand-600 hover:text-brand-800"
                  >
                    {showSecondPerson ? '− Remove second person' : '+ Add second responsible person (optional)'}
                  </button>
                </div>

                {/* Second Person Selection */}
                {showSecondPerson && (
                  <div className="mb-4 p-3 bg-steel-50 rounded-xl">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      👤 Second Responsible Person (optional)
                    </label>
                    <select
                      value={selectedPerson2Id}
                      onChange={(e) => setSelectedPerson2Id(e.target.value)}
                      className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">-- Select person --</option>
                      {users.filter(u => u.id.toString() !== selectedPersonId).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500"
                    placeholder="Reason for change, handover notes, etc..."
                  />
                </div>

                {/* Summary */}
                {selectedPersonId && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-800">
                      <strong>Summary:</strong> Responsibility for {outstandingItems.length} item(s) will be transferred to{' '}
                      <strong>{getSelectedPersonName(selectedPersonId)}</strong>
                      {selectedPerson2Id && (
                        <> and <strong>{getSelectedPersonName(selectedPerson2Id)}</strong></>
                      )}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !selectedPersonId}
                    className={`px-4 py-2 text-white rounded-xl transition-colors ${
                      isLoading || !selectedPersonId
                        ? 'bg-steel-400 cursor-not-allowed'
                        : 'bg-brand-600 hover:bg-brand-700'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Changing...
                      </span>
                    ) : (
                      'Change Responsible Person'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column - Items in Car */}
            <div className="lg:w-1/2">
              <div className="h-full border rounded-xl bg-steel-50">
                <div className="p-3 border-b bg-steel-100 rounded-t-lg">
                  <h4 className="text-sm font-medium text-slate-700">
                    Items in Car ({outstandingItems.length})
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Items will remain in the car. Only the responsible person will change.
                  </p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {outstandingItems.length === 0 ? (
                    <div className="px-3 py-8 text-center text-slate-500 text-sm">
                      No items in car
                    </div>
                  ) : (
                    outstandingItems.map((item, index) => (
                      <div key={index} className="px-3 py-2 border-b last:border-b-0 flex items-center gap-3 hover:bg-steel-100">
                        {/* Item Picture */}
                        <div className="flex-shrink-0 w-12 h-12 bg-steel-200 rounded overflow-hidden">
                          {item.item_picture ? (
                            <img
                              src={item.item_picture}
                              alt={item.item_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        {/* Item Info */}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-slate-900 block truncate">{item.item_name}</span>
                          {item.item_sku && (
                            <span className="text-xs text-slate-500">SKU: {item.item_sku}</span>
                          )}
                        </div>
                        {/* Quantity */}
                        <span className="text-sm font-medium text-brand-600 whitespace-nowrap">
                          {item.quantityOutstanding} szt.
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseHistory;
